import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArchiveBoxIcon,
  ArrowDownTrayIcon,
  ArrowsRightLeftIcon,
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  Cog6ToothIcon,
  DocumentArrowUpIcon,
  DocumentMagnifyingGlassIcon,
  KeyIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  PlusIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import Layout from '../../components/layout/Layout';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DiffViewer,
  Input,
  Label,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Page,
  PageBody,
  PageHeader,
  Progress,
  Select,
  SelectOption,
  Spinner,
  Textarea,
} from '../../components/ui';
import { AXIOS_INSTANCE } from '../../services/api-client';
import { useToasts } from '../../stores/useToasts';
import { buildDraftMarkdown } from './prdDraftMarkdown';

type OutputMode = 'product';
type PRDPanel = 'setup' | 'chat' | 'draft' | 'context' | 'prd';
type CodexLaunchTarget = 'cloud' | 'desktop';
type CodexCloudLoginStatus = 'idle' | 'pending' | 'authenticated' | 'failed';

interface CodexLoginPrompt {
  title: string;
  message: string;
  command?: string;
  localDockerCommand?: string;
  detail?: string;
  alertTitle?: string;
}

interface CodexCloudLoginResponse {
  status: CodexCloudLoginStatus;
  message: string;
  verification_url?: string | null;
  user_code?: string | null;
  expires_in_seconds?: number | null;
  detail?: string | null;
}

interface CodexLaunchRequest {
  jobId: string;
  prdMarkdown: string;
  launchTarget: CodexLaunchTarget;
  interactiveLoginAttempted?: boolean;
}

interface ArchitectureSection {
  key: string;
  title: string;
  summary: string;
  relevant_paths: string[];
  follow_up_axes: string[];
}

interface ArchitectureSummary {
  repository_label: string;
  summary_markdown: string;
  highlighted_sections: ArchitectureSection[];
  suggested_focus_paths: string[];
  source_documents: string[];
}

interface ClarificationTurn {
  role: 'user' | 'assistant';
  content: string;
}

interface ClarificationState {
  turns: ClarificationTurn[];
  question_budget: number;
  question_count: number;
  asked_questions: string[];
  pending_followup_questions: string[];
  resolved_requirements: string[];
  assumptions?: string[];
  suggested_focus_paths: string[];
  ready_to_generate: boolean;
  clarified_brief: string;
  reasoning?: Record<string, any>;
}

interface PRDAttachment {
  id: number;
  session_id: string;
  original_filename: string;
  mime_type: string;
  file_size: number;
  parse_status: 'queued' | 'processing' | 'completed' | 'failed';
  parse_error?: string | null;
  chunk_count: number;
  index_namespace?: string | null;
  artifact_json: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface PRDVersion {
  id: number;
  job_id: string;
  session_id: string;
  version_number: number;
  title: string;
  sections_json: Record<string, any>;
  executive_markdown: string;
  implementation_markdown: string;
  grounding_report_json: Record<string, any>;
  export_metadata_json: Record<string, any>;
  created_at: string;
}

interface PRDJob {
  job_id: string;
  session_id: string;
  status: string;
  output_mode: OutputMode;
  progress_percentage: number;
  current_stage?: string | null;
  error_message?: string | null;
  cursor_agent_url?: string | null;
  repository_url?: string | null;
  branch?: string | null;
  prompt?: string | null;
  instruction?: string | null;
  request_payload_json: Record<string, any>;
  grounding_report_json: Record<string, any>;
  latest_version?: PRDVersion | null;
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
}

interface PRDCodexRun {
  run_id: string;
  job_id: string;
  session_id: string;
  version_id?: number | null;
  launch_target?: CodexLaunchTarget;
  status: 'queued' | 'running' | 'completed' | 'failed';
  current_stage?: string | null;
  branch_name?: string | null;
  worktree_path?: string | null;
  codex_thread_id?: string | null;
  cloud_url?: string | null;
  model?: string | null;
  sandbox_mode?: string | null;
  approval_policy?: string | null;
  error_message?: string | null;
  request_payload_json: Record<string, any>;
  result_payload_json: Record<string, any>;
  final_response?: string | null;
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
}

interface PRDCodexRunListResponse {
  items: PRDCodexRun[];
  total: number;
}

interface PRDSessionSummary {
  session_id: string;
  title: string;
  repository_url?: string | null;
  branch?: string | null;
  focus_paths: string[];
  output_mode: OutputMode;
  cursor_provider_config_id?: number | null;
  llm_provider_config_id?: number | null;
  llm_model?: string | null;
  latest_prompt_summary?: string | null;
  ready_to_generate: boolean;
  is_archived: boolean;
  latest_job_status?: string | null;
  latest_job_id?: string | null;
  attachment_count: number;
  created_at: string;
  updated_at: string;
}

interface PRDSession extends PRDSessionSummary {
  metadata_json: Record<string, any>;
  architecture_summary?: ArchitectureSummary | null;
  clarification_state?: ClarificationState | null;
  evidence_state_json: Record<string, any>;
  attachments: PRDAttachment[];
  latest_job?: PRDJob | null;
}

interface SessionListResponse {
  items: PRDSessionSummary[];
  total: number;
}

interface SessionTurnResponse {
  session: PRDSession;
  assistant_message: string;
  questions: string[];
  ready_to_generate: boolean;
  clarified_brief: string;
  pending_followup_questions: string[];
  resolved_requirements: string[];
  assumptions?: string[];
  suggested_focus_paths: string[];
  reasoning?: Record<string, any>;
}

interface SessionCreateRequest {
  title?: string | null;
  repository_url?: string | null;
  branch: string;
  focus_paths: string[];
  output_mode: OutputMode;
  external_context?: string | null;
  cursor_provider_config_id?: number | null;
  llm_provider_config_id?: number | null;
  llm_model?: string | null;
}

interface ProviderConfiguration {
  id: number;
  provider_type: string;
  name?: string | null;
  is_enabled: boolean;
  available_models?: string[];
  default_model?: string | null;
  is_shared_from_platform?: boolean;
}

interface ProviderListResponse {
  providers: ProviderConfiguration[];
  total_count: number;
}

interface PRDGeneratorConfig {
  selected_cursor_provider_id?: number | null;
  selected_codex_cloud_provider_id?: number | null;
  cursor_provider_configured?: boolean;
  codex_cloud_provider_configured?: boolean;
  providers?: ProviderConfiguration[];
}

interface PRDJobStatus {
  job_id: string;
  status: string;
  stage?: string | null;
  stages_completed: string[];
  stages_remaining: string[];
  estimated_seconds_remaining?: number | null;
  cursor_agent_url?: string | null;
  error_message?: string | null;
}

interface PRDQualityEvaluation {
  score: number;
  passed: boolean;
  readiness: string;
  dimension_scores: Record<string, number>;
  section_scores?: Record<string, number>;
  section_feedback?: Record<string, string[]>;
  strengths: string[];
  issues: string[];
  recommended_next_questions: string[];
  assumptions: string[];
  judge_model?: string | null;
  used_fallback: boolean;
  evaluated_at: string;
}

interface CodexLaunchResponse {
  run_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  message: string;
  launch_target?: CodexLaunchTarget;
  cloud_url?: string | null;
  desktop_url?: string | null;
  handoff_prompt?: string | null;
  reused?: boolean;
}

const API_BASE = '/v1/prd-generator';
const DEFAULT_REPO_URL = 'https://github.com/eliza-hq/ElizaPlatform';
const DEFAULT_BRANCH = 'dev';
const TERMINAL_STATUSES = new Set(['completed', 'failed']);
const REVIEW_READY_STORAGE_KEY = 'prd-generator-review-ready-by-version';
const QUALITY_EVALUATION_STORAGE_KEY = 'prd-generator-quality-evaluations-by-version';
const ASSUMPTION_READY_SCORE = 75;
const TARGET_READINESS_SCORE = 90;
const MAX_DRAFT_CHARACTERS = 20_000;
const GAP_ANALYSIS_REQUEST = [
  'Review the existing PRD draft as a product requirements refiner.',
  'Identify what is already decided, surface only material gaps, and ask focused follow-up questions that fill those gaps.',
  'Keep refining until the PRD is at least 90% thorough, using clearly labeled assumptions only for low-risk decisions.',
].join(' ');
const CODEX_HANDOFF_TEMPLATE_PATHS = [
  'features/_templates/02_TECHNICAL_SPEC_TEMPLATE.md',
  'features/_templates/03_IMPLEMENTATION_PLAN_TEMPLATE.md',
];

function clarificationReadinessScore(state?: ClarificationState | null): number {
  const rawScore = state?.reasoning?.readiness?.score;
  const score = typeof rawScore === 'number' ? rawScore : Number(rawScore || 0);
  if (!Number.isFinite(score)) return 0;
  return Math.max(0, Math.min(100, score));
}

function humanizeProviderType(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function providerDisplayName(provider: ProviderConfiguration, fallback: string): string {
  return provider.name?.trim() || fallback || humanizeProviderType(provider.provider_type);
}

function buildGapAnalysisRequest(draft: string, hasAttachments: boolean): string {
  const sourceNotes = [
    draft.trim() ? 'Use the inline PRD draft included below as the source of truth.' : '',
    hasAttachments ? 'Also inspect the attached PRD document and reconcile it with the inline draft.' : '',
  ].filter(Boolean).join(' ');
  const inlineDraft = draft.trim()
    ? `\n\n## Existing PRD draft\n\n${draft.trim()}`
    : '';
  return `${GAP_ANALYSIS_REQUEST} ${sourceNotes}`.trim() + inlineDraft;
}

function clarificationTurnDisplayContent(turn: ClarificationTurn): string {
  if (turn.role === 'user' && turn.content.startsWith(GAP_ANALYSIS_REQUEST)) {
    return 'Analyze my current PRD draft and identify the highest-impact gaps.';
  }
  return turn.content;
}

function loadReviewReadyState(): Record<string, boolean> {
  if (typeof window === 'undefined') return {};
  try {
    const value = window.localStorage.getItem(REVIEW_READY_STORAGE_KEY);
    return value ? JSON.parse(value) : {};
  } catch {
    return {};
  }
}

function saveReviewReadyState(state: Record<string, boolean>) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(REVIEW_READY_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Local storage is best-effort; the in-memory state still updates the UI.
  }
}

function loadQualityEvaluationState(): Record<string, PRDQualityEvaluation> {
  if (typeof window === 'undefined') return {};
  try {
    const value = window.localStorage.getItem(QUALITY_EVALUATION_STORAGE_KEY);
    return value ? JSON.parse(value) : {};
  } catch {
    return {};
  }
}

function saveQualityEvaluationState(state: Record<string, PRDQualityEvaluation>) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(QUALITY_EVALUATION_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Local storage is best-effort; the in-memory state still keeps the score stable.
  }
}

function requestErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: unknown } }).response;
    const data = response?.data;
    if (data && typeof data === 'object' && 'detail' in data) {
      const detail = (data as { detail?: unknown }).detail;
      if (typeof detail === 'string' && detail.trim()) return detail;
    }
  }
  return fallback;
}

async function fetchSessionsWithOptions(includeArchived: boolean): Promise<SessionListResponse> {
  const { data } = await AXIOS_INSTANCE.get<SessionListResponse>(`${API_BASE}/sessions`, {
    params: includeArchived ? { include_archived: true, archived_only: true } : undefined,
  });
  return data;
}

async function fetchProviderConfigurations(): Promise<ProviderListResponse> {
  const { data } = await AXIOS_INSTANCE.get<ProviderListResponse>('/v1/providers/configurations/with-shared');
  return data;
}

async function fetchPrdGeneratorConfig(): Promise<PRDGeneratorConfig> {
  const { data } = await AXIOS_INSTANCE.get<PRDGeneratorConfig>(`${API_BASE}/config`);
  return data;
}

async function updatePrdGeneratorConfig(payload: {
  selected_cursor_provider_id?: number | null;
  selected_codex_cloud_provider_id?: number | null;
}): Promise<PRDGeneratorConfig> {
  const { data } = await AXIOS_INSTANCE.put<PRDGeneratorConfig>(`${API_BASE}/config`, payload);
  return data;
}

async function fetchSession(sessionId: string): Promise<PRDSession> {
  const { data } = await AXIOS_INSTANCE.get<PRDSession>(`${API_BASE}/sessions/${sessionId}`);
  return data;
}

async function createSession(payload: SessionCreateRequest): Promise<PRDSession> {
  const { data } = await AXIOS_INSTANCE.post<PRDSession>(`${API_BASE}/sessions`, payload);
  return data;
}

async function startPrdSession(payload: {
  request: string;
  title?: string | null;
  repository_url: string;
  branch: string;
  focus_paths: string[];
  output_mode: OutputMode;
  external_context?: string | null;
  attachment_session_id?: string | null;
  cursor_provider_config_id?: number | null;
  llm_provider_config_id?: number | null;
  llm_model?: string | null;
}): Promise<SessionTurnResponse> {
  const { data } = await AXIOS_INSTANCE.post<SessionTurnResponse>(`${API_BASE}/sessions/start`, payload);
  return data;
}

async function continuePrdSession(sessionId: string, message: string): Promise<SessionTurnResponse> {
  const { data } = await AXIOS_INSTANCE.post<SessionTurnResponse>(`${API_BASE}/sessions/${sessionId}/messages`, { message });
  return data;
}

async function generatePrd(
  sessionId: string,
  payload: {
    output_mode?: OutputMode;
    force?: boolean;
    external_context?: string | null;
    cursor_provider_config_id?: number | null;
    llm_provider_config_id?: number | null;
    llm_model?: string | null;
  },
): Promise<{ job_id: string; session_id: string; status: string; message: string }> {
  const { data } = await AXIOS_INSTANCE.post(`${API_BASE}/sessions/${sessionId}/generate`, payload);
  return data;
}

async function uploadAttachment(sessionId: string, file: File): Promise<PRDAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await AXIOS_INSTANCE.post<PRDAttachment>(
    `${API_BASE}/sessions/${sessionId}/attachments`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

async function fetchJob(jobId: string): Promise<PRDJob> {
  const { data } = await AXIOS_INSTANCE.get<PRDJob>(`${API_BASE}/jobs/${jobId}`);
  return data;
}

async function fetchJobStatus(jobId: string): Promise<PRDJobStatus> {
  const { data } = await AXIOS_INSTANCE.get<PRDJobStatus>(`${API_BASE}/jobs/${jobId}/status`);
  return data;
}

async function fetchCodexRuns(jobId: string): Promise<PRDCodexRunListResponse> {
  const { data } = await AXIOS_INSTANCE.get<PRDCodexRunListResponse>(`${API_BASE}/jobs/${jobId}/codex-runs?scope=session`);
  return data;
}

async function launchCodexRun(
  jobId: string,
  prdMarkdown: string | undefined,
  launchTarget: CodexLaunchTarget,
): Promise<CodexLaunchResponse> {
  const { data } = await AXIOS_INSTANCE.post(`${API_BASE}/jobs/${jobId}/codex-runs`, {
    prd_markdown: prdMarkdown ?? null,
    launch_target: launchTarget,
  });
  return data;
}

async function startCodexCloudLogin(): Promise<CodexCloudLoginResponse> {
  const { data } = await AXIOS_INSTANCE.post<CodexCloudLoginResponse>(`${API_BASE}/codex-cloud/login`);
  return data;
}

async function fetchCodexCloudLogin(signal?: AbortSignal): Promise<CodexCloudLoginResponse> {
  const { data } = await AXIOS_INSTANCE.get<CodexCloudLoginResponse>(
    `${API_BASE}/codex-cloud/login`,
    { signal },
  );
  return data;
}

async function evaluateSessionQuality(sessionId: string, prdMarkdown?: string): Promise<PRDQualityEvaluation> {
  const { data } = await AXIOS_INSTANCE.post<PRDQualityEvaluation>(
    `${API_BASE}/sessions/${sessionId}/evaluate`,
    { prd_markdown: prdMarkdown ?? null },
  );
  return data;
}

async function archiveSessionApi(sessionId: string): Promise<PRDSession> {
  const { data } = await AXIOS_INSTANCE.post<PRDSession>(`${API_BASE}/sessions/${sessionId}/archive`);
  return data;
}

async function restoreSessionApi(sessionId: string): Promise<PRDSession> {
  const { data } = await AXIOS_INSTANCE.post<PRDSession>(`${API_BASE}/sessions/${sessionId}/restore`);
  return data;
}

async function deleteSessionApi(sessionId: string): Promise<void> {
  await AXIOS_INSTANCE.delete(`${API_BASE}/sessions/${sessionId}`);
}

async function updateSessionConfiguration(
  sessionId: string,
  payload: SessionCreateRequest,
): Promise<PRDSession> {
  const { data } = await AXIOS_INSTANCE.patch<PRDSession>(`${API_BASE}/sessions/${sessionId}/configuration`, payload);
  return data;
}

function parseFocusPaths(raw: string): string[] {
  return raw.split(/\r?\n|,/).map((item) => item.trim()).filter(Boolean);
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  const maybeError = error as { response?: { data?: { detail?: unknown; message?: unknown } }; message?: unknown };
  const detail = maybeError.response?.data?.detail || maybeError.response?.data?.message || maybeError.message;
  if (detail && typeof detail === 'object' && 'message' in detail && typeof (detail as { message?: unknown }).message === 'string') {
    return (detail as { message: string }).message;
  }
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
}

function getCodexLoginPrompt(error: unknown): CodexLoginPrompt | null {
  const maybeError = error as { response?: { data?: { detail?: unknown; message?: unknown } }; message?: unknown };
  const detail = maybeError.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    const payload = detail as {
      code?: unknown;
      title?: unknown;
      message?: unknown;
      command?: unknown;
      local_docker_command?: unknown;
      detail?: unknown;
    };
    if (payload.code === 'codex_cloud_login_required' || payload.code === 'codex_cloud_access_token_invalid') {
      const isTokenInvalid = payload.code === 'codex_cloud_access_token_invalid';
      return {
        title: typeof payload.title === 'string'
          ? payload.title
          : isTokenInvalid ? 'Codex Cloud access token was rejected' : 'Codex Cloud login required',
        message: typeof payload.message === 'string'
          ? payload.message
          : isTokenInvalid
            ? 'Re-enter a valid Codex Cloud access token, then test the provider again.'
            : 'Complete Codex CLI login in the backend environment before this Codex Cloud handoff can start.',
        command: typeof payload.command === 'string' ? payload.command : isTokenInvalid ? undefined : 'codex login --device-auth',
        localDockerCommand: typeof payload.local_docker_command === 'string' ? payload.local_docker_command : undefined,
        detail: typeof payload.detail === 'string' ? payload.detail : undefined,
        alertTitle: isTokenInvalid ? 'Provider token needs attention' : 'One-time backend login needed',
      };
    }
  }

  const message = getApiErrorMessage(error, '');
  const normalized = message.toLowerCase();
  const isInvalidApiKey = (
    normalized.includes('incorrect api key')
    || normalized.includes('invalid api key')
    || normalized.includes('invalid_api_key')
  );
  if (isInvalidApiKey) {
    return {
      title: 'Codex Cloud access token was rejected',
      message: 'Re-enter a valid Codex Cloud access token, then test the provider again.',
      detail: message,
      alertTitle: 'Provider token needs attention',
    };
  }
  if (
    normalized.includes('codex login --device-auth')
    || normalized.includes('not signed in')
    || normalized.includes('not logged in')
    || normalized.includes('automatic codex cloud login did not complete')
    || normalized.includes('one-time codex cli login')
    || normalized.includes('backend codex cli is not authenticated')
  ) {
    return {
      title: 'Codex Cloud login required',
      message: 'Complete a one-time Codex CLI login in the backend environment, then retry this handoff.',
      command: 'codex login --device-auth',
      localDockerCommand: 'docker compose -f docker/docker-compose.yml -f docker/docker-compose.override.yml exec app /workspace/scripts/codex_runner/node_modules/.bin/codex login --device-auth',
      detail: message,
      alertTitle: 'One-time backend login needed',
    };
  }
  return null;
}

function formatQualityDimension(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatTimestamp(value?: string | null): string {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

function codexDesktopUrlFromRun(run?: PRDCodexRun | null): string | null {
  const value = run?.result_payload_json?.desktop_url;
  return typeof value === 'string' && value.trim() && codexRunHasTemplatePaths(run) ? value : null;
}

function codexRunKind(run?: PRDCodexRun | null): CodexLaunchTarget | null {
  if (run?.launch_target === 'cloud' || run?.launch_target === 'desktop') return run.launch_target;
  const resultKind = run?.result_payload_json?.handoff_kind;
  const requestKind = run?.request_payload_json?.handoff_kind;
  if (resultKind === 'codex_cloud' || requestKind === 'codex_cloud' || run?.codex_thread_id) return 'cloud';
  if (resultKind === 'codex_desktop' || requestKind === 'codex_desktop') return 'desktop';
  return null;
}

function codexCloudUrlFromRun(run?: PRDCodexRun | null): string | null {
  const candidates = [
    run?.cloud_url,
    run?.result_payload_json?.cloud_url,
    run?.request_payload_json?.cloud_url,
  ];
  for (const value of candidates) {
    if (
      typeof value === 'string'
      && /^https:\/\/chatgpt\.com\/codex\/(?:cloud\/)?tasks\/task_[A-Za-z0-9_-]+(?:[/?#].*)?$/.test(value)
    ) return value;
  }
  return run?.codex_thread_id?.startsWith('task_')
    ? `https://chatgpt.com/codex/tasks/${run.codex_thread_id}`
    : null;
}

function codexCloudRunNeedsUrlRecovery(run?: PRDCodexRun | null): boolean {
  const requestPayload = run?.request_payload_json || {};
  return Boolean(
    run?.status === 'completed'
    && !codexCloudUrlFromRun(run)
    && !requestPayload.cloud_auth_mode
    && !requestPayload.cloud_url_recovery_started_at,
  );
}

function codexRunHasTemplatePaths(run?: PRDCodexRun | null): boolean {
  const payload = run?.result_payload_json || {};
  const prompt = typeof payload.handoff_prompt === 'string' ? payload.handoff_prompt : '';
  const templatePaths = Array.isArray(payload.handoff_template_paths)
    ? payload.handoff_template_paths.filter((path): path is string => typeof path === 'string')
    : [];
  return CODEX_HANDOFF_TEMPLATE_PATHS.every((path) => prompt.includes(path) || templatePaths.includes(path));
}

function buildSessionPayload(input: {
  title: string;
  repositoryUrl: string;
  branch: string;
  focusPathsText: string;
  outputMode?: OutputMode;
  externalContextText: string;
  cursorProviderConfigId: string;
  llmProviderConfigId: string;
  llmModel: string;
}): SessionCreateRequest {
  return {
    title: input.title.trim() || null,
    repository_url: input.repositoryUrl.trim() || null,
    branch: input.branch.trim() || DEFAULT_BRANCH,
    focus_paths: parseFocusPaths(input.focusPathsText),
    output_mode: 'product',
    external_context: input.externalContextText.trim() || null,
    cursor_provider_config_id: input.cursorProviderConfigId ? Number(input.cursorProviderConfigId) : null,
    llm_provider_config_id: input.llmProviderConfigId ? Number(input.llmProviderConfigId) : null,
    llm_model: input.llmModel.trim() || null,
  };
}

function mergeTurnAssumptionsIntoSession(response: SessionTurnResponse): PRDSession {
  const turnAssumptions = (response.assumptions || []).map((item) => item.trim()).filter(Boolean);
  if (!turnAssumptions.length || !response.session.clarification_state) {
    return response.session;
  }

  const stateAssumptions = (response.session.clarification_state.assumptions || [])
    .map((item) => item.trim())
    .filter(Boolean);
  const assumptions = Array.from(new Set([...stateAssumptions, ...turnAssumptions]));
  return {
    ...response.session,
    clarification_state: {
      ...response.session.clarification_state,
      assumptions,
    },
  };
}

export default function PRDGeneratorPage() {
  const queryClient = useQueryClient();
  const { push: addToast } = useToasts();
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const codexCloudWindowRef = useRef<Window | null>(null);

  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [pendingJob, setPendingJob] = useState<{ sessionId: string; jobId: string } | null>(null);
  const [pendingCodexJobId, setPendingCodexJobId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [repositoryUrl, setRepositoryUrl] = useState(DEFAULT_REPO_URL);
  const [branch, setBranch] = useState(DEFAULT_BRANCH);
  const [focusPathsText, setFocusPathsText] = useState('');
  const [savedDraftMarkdown, setSavedDraftMarkdown] = useState('');
  const [outputMode, setOutputMode] = useState<OutputMode>('product');
  const [replyText, setReplyText] = useState('');
  const [activePanel, setActivePanel] = useState<PRDPanel>('setup');
  const [showArchived, setShowArchived] = useState(false);
  const [isDraftMode, setIsDraftMode] = useState(true);
  const [, setSetupExpanded] = useState(true);
  const [sessionsCollapsed, setSessionsCollapsed] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [codexLoginPrompt, setCodexLoginPrompt] = useState<CodexLoginPrompt | null>(null);
  const [showCodexCloudLogin, setShowCodexCloudLogin] = useState(false);
  const [codexCloudLoginPolling, setCodexCloudLoginPolling] = useState(false);
  const [pendingCodexCloudLaunch, setPendingCodexCloudLaunch] = useState<CodexLaunchRequest | null>(null);
  const [codexLaunchTarget, setCodexLaunchTarget] = useState<CodexLaunchTarget>('cloud');
  const [cursorProviderConfigId, setCursorProviderConfigId] = useState('');
  const [llmProviderConfigId, setLlmProviderConfigId] = useState('');
  const [llmModel, setLlmModel] = useState('');
  const [codexCloudProviderConfigId, setCodexCloudProviderConfigId] = useState('');
  const [draftMarkdown, setDraftMarkdown] = useState('');
  const [draftDirty, setDraftDirty] = useState(false);
  const [showVersionCompare, setShowVersionCompare] = useState(false);
  const [prdOutputMarkdown, setPrdOutputMarkdown] = useState('');
  const [prdOutputDirty, setPrdOutputDirty] = useState(false);
  const [reviewReadyByVersion, setReviewReadyByVersion] = useState<Record<string, boolean>>(loadReviewReadyState);
  const [qualityEvaluationsByVersion, setQualityEvaluationsByVersion] = useState<Record<string, PRDQualityEvaluation>>(
    loadQualityEvaluationState,
  );
  const [optimisticUserTurn, setOptimisticUserTurn] = useState<{
    sessionId: string | null;
    content: string;
    submittedAt: number;
  } | null>(null);
  const lastDraftSourceKeyRef = useRef('');
  const autoScoredVersionKeysRef = useRef<Set<string>>(new Set());
  const sessionsQuery = useQuery({
    queryKey: ['prd-sessions', showArchived],
    queryFn: () => fetchSessionsWithOptions(showArchived),
    networkMode: 'always',
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!isDraftMode && !selectedSessionId && sessionsQuery.data?.items.length) {
      setSelectedSessionId(sessionsQuery.data.items[0].session_id);
    }
  }, [isDraftMode, selectedSessionId, sessionsQuery.data]);

  const sessionQuery = useQuery({
    queryKey: ['prd-session', selectedSessionId],
    queryFn: () => fetchSession(selectedSessionId!),
    enabled: !!selectedSessionId,
    refetchInterval: (query) => {
      const data = query.state.data as PRDSession | undefined;
      const hasPendingAttachments = Boolean(
        data?.attachments?.some((attachment) => attachment.parse_status === 'queued' || attachment.parse_status === 'processing'),
      );
      return hasPendingAttachments ? 2500 : false;
    },
  });
  const activeSession = !isDraftMode && sessionQuery.data?.session_id === selectedSessionId
    ? sessionQuery.data
    : null;

  const providersQuery = useQuery({
    queryKey: ['providers', 'configurations-with-shared'],
    queryFn: fetchProviderConfigurations,
    networkMode: 'always',
    retry: false,
    staleTime: 5 * 60_000,
  });

  const prdConfigQuery = useQuery({
    queryKey: ['prd-generator-config'],
    queryFn: fetchPrdGeneratorConfig,
    networkMode: 'always',
    retry: false,
    staleTime: 60_000,
  });

  const providerOptions = useMemo(
    () => providersQuery.data?.providers || [],
    [providersQuery.data?.providers],
  );
  const cursorProviderOptions = useMemo(
    () => providerOptions.filter((provider) => provider.is_enabled && provider.provider_type === 'cursor'),
    [providerOptions],
  );
  const llmProviderOptions = useMemo(
    () => providerOptions.filter(
      (provider) => provider.is_enabled && !['cursor', 'codex_cloud'].includes(provider.provider_type),
    ),
    [providerOptions],
  );
  const codexCloudProviderOptions = useMemo(
    () => providerOptions.filter((provider) => provider.is_enabled && provider.provider_type === 'codex_cloud'),
    [providerOptions],
  );
  const selectedCursorProvider = useMemo(
    () => cursorProviderOptions.find((provider) => String(provider.id) === cursorProviderConfigId),
    [cursorProviderConfigId, cursorProviderOptions],
  );
  const selectedLlmProvider = useMemo(
    () => llmProviderOptions.find((provider) => String(provider.id) === llmProviderConfigId),
    [llmProviderConfigId, llmProviderOptions],
  );
  const repositoryProviderPlaceholder = cursorProviderOptions.length
    ? 'Select repository provider'
    : 'No repository provider configured';
  const writingProviderPlaceholder = llmProviderOptions.length
    ? 'Select writing provider'
    : 'No writing provider configured';
  const codexCloudProviderPlaceholder = codexCloudProviderOptions.length
    ? 'Select Codex Cloud provider'
    : 'No Codex Cloud provider configured';

  useEffect(() => {
    if (!activeSession) return;
    setTitle(activeSession.title || '');
    setRepositoryUrl(activeSession.repository_url || DEFAULT_REPO_URL);
    setBranch(activeSession.branch || DEFAULT_BRANCH);
    setFocusPathsText((activeSession.focus_paths || []).join('\n'));
    setSavedDraftMarkdown(String(activeSession.metadata_json?.external_context || ''));
    setOutputMode('product');
    setCursorProviderConfigId(activeSession.cursor_provider_config_id ? String(activeSession.cursor_provider_config_id) : '');
    setLlmProviderConfigId(activeSession.llm_provider_config_id ? String(activeSession.llm_provider_config_id) : '');
    setLlmModel(activeSession.llm_model || '');
  }, [activeSession?.session_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const config = prdConfigQuery.data;
    if (!config) return;
    if (!activeSession && config.selected_cursor_provider_id) {
      setCursorProviderConfigId(String(config.selected_cursor_provider_id));
    }
    if (config.selected_codex_cloud_provider_id) {
      setCodexCloudProviderConfigId(String(config.selected_codex_cloud_provider_id));
    }
  }, [
    activeSession,
    prdConfigQuery.data,
  ]);

  useEffect(() => {
    if (!cursorProviderConfigId && cursorProviderOptions.length > 0) {
      setCursorProviderConfigId(String(cursorProviderOptions[0].id));
    }
    if (!llmProviderConfigId && llmProviderOptions.length > 0) {
      const provider = llmProviderOptions[0];
      setLlmProviderConfigId(String(provider.id));
      setLlmModel(provider.default_model || provider.available_models?.[0] || '');
    }
    if (!codexCloudProviderConfigId && codexCloudProviderOptions.length > 0) {
      setCodexCloudProviderConfigId(String(codexCloudProviderOptions[0].id));
    }
  }, [
    codexCloudProviderConfigId,
    codexCloudProviderOptions,
    cursorProviderConfigId,
    cursorProviderOptions,
    llmProviderConfigId,
    llmProviderOptions,
  ]);

  const selectedPendingJobId = pendingJob && activeSession && pendingJob.sessionId === activeSession.session_id
    ? pendingJob.jobId
    : null;
  const currentJobId = selectedPendingJobId || activeSession?.latest_job?.job_id || null;
  const baseCurrentJobStatus = selectedPendingJobId ? 'queued' : activeSession?.latest_job?.status;

  const jobQuery = useQuery({
    queryKey: ['prd-job', currentJobId],
    queryFn: () => fetchJob(currentJobId!),
    enabled: !!currentJobId,
    refetchInterval: (query) => {
      const status = (query.state.data as PRDJob | undefined)?.status || baseCurrentJobStatus;
      return currentJobId && !TERMINAL_STATUSES.has(status || '') ? 4000 : false;
    },
  });

  const jobStatusQuery = useQuery({
    queryKey: ['prd-job-status', currentJobId],
    queryFn: () => fetchJobStatus(currentJobId!),
    enabled: !!currentJobId,
    refetchInterval: (query) => {
      const status = (query.state.data as PRDJobStatus | undefined)?.status || jobQuery.data?.status || baseCurrentJobStatus;
      return currentJobId && !TERMINAL_STATUSES.has(status || '') ? 4000 : false;
    },
  });

  useEffect(() => {
    const status = jobQuery.data?.status;
    if (!currentJobId || !status || !TERMINAL_STATUSES.has(status)) return;
    const selectedSessionHasGeneratedJob = activeSession?.latest_job?.job_id === currentJobId;
    if (pendingJob?.jobId === currentJobId && selectedSessionHasGeneratedJob) {
      setPendingJob(null);
    }
    if (selectedSessionId) {
      queryClient.invalidateQueries({ queryKey: ['prd-session', selectedSessionId] });
    }
    queryClient.invalidateQueries({ queryKey: ['prd-sessions'] });
  }, [
    activeSession?.latest_job?.job_id,
    currentJobId,
    jobQuery.data?.status,
    pendingJob?.jobId,
    queryClient,
    selectedSessionId,
  ]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [activeSession?.clarification_state?.turns?.length, optimisticUserTurn?.submittedAt]);

  const codexRunsQuery = useQuery({
    queryKey: ['prd-codex-runs', currentJobId],
    queryFn: () => fetchCodexRuns(currentJobId!),
    enabled: !!currentJobId,
    refetchInterval: (query) => {
      const data = query.state.data as PRDCodexRunListResponse | undefined;
      return data?.items?.some((run) => !TERMINAL_STATUSES.has(run.status)) ? 4000 : false;
    },
  });

  const codexCloudLoginQuery = useQuery({
    queryKey: ['prd-codex-cloud-login'],
    queryFn: ({ signal }) => fetchCodexCloudLogin(signal),
    enabled: showCodexCloudLogin && codexCloudLoginPolling,
    retry: false,
    refetchIntervalInBackground: true,
    refetchInterval: (query) => {
      const data = query.state.data as CodexCloudLoginResponse | undefined;
      return data?.status === 'authenticated' || data?.status === 'failed' ? false : 2000;
    },
  });

  const codexCloudLoginMutation = useMutation({
    mutationFn: startCodexCloudLogin,
    onMutate: () => {
      setCodexCloudLoginPolling(false);
      setShowCodexCloudLogin(true);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['prd-codex-cloud-login'], data);
      setCodexCloudLoginPolling(data.status === 'pending');
      if (data.verification_url) {
        const cloudWindow = codexCloudWindowRef.current;
        if (cloudWindow && !cloudWindow.closed) {
          cloudWindow.location.href = data.verification_url;
        } else {
          codexCloudWindowRef.current = window.open(
            data.verification_url,
            '_blank',
            'noopener,noreferrer',
          );
        }
      }
    },
    onError: (error) => {
      setCodexCloudLoginPolling(false);
      if (codexCloudWindowRef.current && !codexCloudWindowRef.current.closed) {
        codexCloudWindowRef.current.close();
      }
      codexCloudWindowRef.current = null;
      addToast({
        kind: 'error',
        title: 'Could not start Codex Cloud login',
        message: getApiErrorMessage(error, 'Could not start Codex Cloud login.'),
      });
    },
  });

  const createSessionMutation = useMutation({
    mutationFn: createSession,
    onSuccess: (session) => {
      setIsDraftMode(false);
      setSelectedSessionId(session.session_id);
      setActivePanel('draft');
      queryClient.invalidateQueries({ queryKey: ['prd-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['prd-session', session.session_id] });
    },
  });

  const updateConfigurationMutation = useMutation({
    mutationFn: async () => {
      const sessionId = activeSession?.session_id;
      const generatorConfig = await updatePrdGeneratorConfig({
        selected_cursor_provider_id: cursorProviderConfigId ? Number(cursorProviderConfigId) : null,
        selected_codex_cloud_provider_id: codexCloudProviderConfigId ? Number(codexCloudProviderConfigId) : null,
      });
      if (!sessionId) {
        return { generatorConfig, session: null };
      }
      const session = await updateSessionConfiguration(
        sessionId,
        buildSessionPayload({
          title,
          repositoryUrl,
          branch,
          focusPathsText,
          outputMode,
          externalContextText: savedDraftMarkdown,
          cursorProviderConfigId,
          llmProviderConfigId,
          llmModel,
        }),
      );
      return { generatorConfig, session };
    },
    onSuccess: ({ generatorConfig, session }) => {
      queryClient.setQueryData(['prd-generator-config'], generatorConfig);
      if (generatorConfig.selected_codex_cloud_provider_id) {
        setCodexCloudProviderConfigId(String(generatorConfig.selected_codex_cloud_provider_id));
      }
      if (session) {
        queryClient.setQueryData(['prd-session', session.session_id], session);
        queryClient.invalidateQueries({ queryKey: ['prd-sessions'] });
      }
      setShowConfigModal(false);
      addToast({ kind: 'success', title: 'Configuration saved', message: 'PRD Refiner setup updated.' });
    },
    onError: (error) => {
      addToast({
        kind: 'error',
        title: 'Save failed',
        message: getApiErrorMessage(error, 'Could not update PRD configuration.'),
      });
    },
  });

  const startSessionMutation = useMutation({
    mutationFn: startPrdSession,
    onSuccess: (data) => {
      const session = mergeTurnAssumptionsIntoSession(data);
      setOptimisticUserTurn(null);
      setIsDraftMode(false);
      setSelectedSessionId(session.session_id);
      setActivePanel('chat');
      setReplyText('');
      queryClient.setQueryData(['prd-session', session.session_id], session);
      queryClient.invalidateQueries({ queryKey: ['prd-sessions'] });
      addToast({ kind: 'success', title: 'Session started', message: 'Clarification questions are ready.' });
    },
    onError: (error) => {
      setOptimisticUserTurn(null);
      addToast({
        kind: 'error',
        title: 'Failed',
        message: requestErrorMessage(error, 'Could not start the PRD clarification session.'),
      });
    },
  });

  const continueSessionMutation = useMutation({
    mutationFn: async (message: string) => {
      const sessionId = activeSession?.session_id;
      if (!sessionId) throw new Error('No session selected');
      return continuePrdSession(sessionId, message);
    },
    onSuccess: (data) => {
      const session = mergeTurnAssumptionsIntoSession(data);
      setOptimisticUserTurn(null);
      setReplyText('');
      queryClient.setQueryData(['prd-session', session.session_id], session);
      queryClient.invalidateQueries({ queryKey: ['prd-sessions'] });
    },
    onError: (error) => {
      setOptimisticUserTurn(null);
      addToast({
        kind: 'error',
        title: 'Failed',
        message: requestErrorMessage(error, 'Could not continue the clarification session.'),
      });
    },
  });

  const uploadAttachmentMutation = useMutation({
    mutationFn: async (files: File[]) => {
      let sessionId = activeSession?.session_id || null;
      if (!sessionId) {
        const created = await createSessionMutation.mutateAsync(
          buildSessionPayload({
            title,
            repositoryUrl,
            branch,
            focusPathsText,
            outputMode,
            externalContextText: draftMarkdown.trim() || savedDraftMarkdown,
            cursorProviderConfigId,
            llmProviderConfigId,
            llmModel,
          }),
        );
        sessionId = created.session_id;
      }
      const uploaded: PRDAttachment[] = [];
      for (const file of files) {
        uploaded.push(await uploadAttachment(sessionId!, file));
      }
      return { sessionId, uploaded };
    },
    onSuccess: ({ sessionId, uploaded }) => {
      setIsDraftMode(false);
      setSelectedSessionId(sessionId);
      setActivePanel('draft');
      queryClient.invalidateQueries({ queryKey: ['prd-session', sessionId] });
      queryClient.invalidateQueries({ queryKey: ['prd-sessions'] });
      addToast({
        kind: 'success',
        title: 'Files queued',
        message: `${uploaded.length} attachment${uploaded.length !== 1 ? 's' : ''} uploaded.`,
      });
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    },
    onError: () => {
      addToast({ kind: 'error', title: 'Upload failed', message: 'Could not upload attachments.' });
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    },
  });

  const generateMutation = useMutation({
    mutationFn: async (options?: { force?: boolean }) => {
      const sessionId = activeSession?.session_id;
      if (!sessionId) throw new Error('No session selected');
      if (draftMarkdown.length > MAX_DRAFT_CHARACTERS) {
        throw new Error(`Drafts are limited to ${MAX_DRAFT_CHARACTERS.toLocaleString()} characters.`);
      }
      const latestDraftMarkdown = draftMarkdown.trim();
      let sourceDraftMarkdown = savedDraftMarkdown.trim();
      if (draftDirty) {
        const session = await updateSessionConfiguration(
          sessionId,
          buildSessionPayload({
            title,
            repositoryUrl,
            branch,
            focusPathsText,
            outputMode,
            externalContextText: latestDraftMarkdown,
            cursorProviderConfigId,
            llmProviderConfigId,
            llmModel,
          }),
        );
        queryClient.setQueryData(['prd-session', session.session_id], session);
        queryClient.invalidateQueries({ queryKey: ['prd-sessions'] });
        setSavedDraftMarkdown(latestDraftMarkdown);
        setDraftDirty(false);
        sourceDraftMarkdown = latestDraftMarkdown;
      }
      return generatePrd(sessionId, {
        output_mode: outputMode,
        force: Boolean(options?.force),
        external_context: sourceDraftMarkdown || null,
        cursor_provider_config_id: cursorProviderConfigId ? Number(cursorProviderConfigId) : null,
        llm_provider_config_id: llmProviderConfigId ? Number(llmProviderConfigId) : null,
        llm_model: llmModel.trim() || null,
      });
    },
    onSuccess: (data) => {
      setPendingJob({ sessionId: data.session_id, jobId: data.job_id });
      setActivePanel('prd');
      queryClient.invalidateQueries({ queryKey: ['prd-session', data.session_id] });
      queryClient.invalidateQueries({ queryKey: ['prd-sessions'] });
      addToast({ kind: 'success', title: 'Generating', message: 'Cursor exploration has started.' });
    },
    onError: (error) => {
      addToast({
        kind: 'error',
        title: 'Failed',
        message: error instanceof Error ? error.message : 'Could not start PRD generation.',
      });
    },
  });

  const codexLaunchMutation = useMutation({
    mutationFn: async (request: CodexLaunchRequest) => {
      setPendingCodexJobId(request.jobId);
      return launchCodexRun(request.jobId, request.prdMarkdown, request.launchTarget);
    },
    onSuccess: (data, request) => {
      queryClient.invalidateQueries({ queryKey: ['prd-codex-runs', request.jobId] });
      if (data.desktop_url) {
        window.location.href = data.desktop_url;
      } else if (data.cloud_url) {
        const cloudWindow = codexCloudWindowRef.current;
        if (cloudWindow && !cloudWindow.closed) {
          cloudWindow.location.href = data.cloud_url;
          codexCloudWindowRef.current = null;
        } else {
          const openedWindow = window.open(data.cloud_url, '_blank', 'noopener,noreferrer');
          if (!openedWindow) {
            window.location.href = data.cloud_url;
          }
        }
      } else if (request.launchTarget === 'cloud') {
        if (codexCloudWindowRef.current && !codexCloudWindowRef.current.closed) {
          codexCloudWindowRef.current.close();
        }
        codexCloudWindowRef.current = null;
      }
      addToast({
        kind: 'success',
        title: data.launch_target === 'desktop'
          ? 'Codex Desktop handoff ready'
          : data.reused
            ? 'Codex Cloud task already exists'
            : 'Codex Cloud task submitted',
        message: data.launch_target === 'desktop'
          ? 'Opening Codex Desktop with the Product Spec.'
          : data.cloud_url
            ? 'Opening the Codex Cloud task for this Product Spec.'
            : 'The existing Codex Cloud task is still being submitted.',
      });
    },
    onError: async (error, request) => {
      const loginPrompt = getCodexLoginPrompt(error);
      const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      const errorCode = detail && typeof detail === 'object' && 'code' in detail
        ? (detail as { code?: unknown }).code
        : null;
      const needsInteractiveCloudLogin = Boolean(
        request.launchTarget === 'cloud'
        && loginPrompt
        && (
          errorCode === 'codex_cloud_login_required'
          || (errorCode === null && loginPrompt.alertTitle === 'One-time backend login needed')
        )
      );
      if (
        needsInteractiveCloudLogin
      ) {
        await queryClient.cancelQueries({ queryKey: ['prd-codex-cloud-login'] });
        codexCloudLoginMutation.reset();
        queryClient.removeQueries({ queryKey: ['prd-codex-cloud-login'] });
        setCodexCloudLoginPolling(false);
        setCodexLoginPrompt(null);
        setPendingCodexCloudLaunch({ ...request, interactiveLoginAttempted: true });
        setShowCodexCloudLogin(true);
        codexCloudLoginMutation.mutate();
        addToast({
          kind: 'info',
          title: 'Reconnect Codex Cloud',
          message: 'Complete ChatGPT authentication and this Product Spec will retry automatically.',
        });
        return;
      }
      if (loginPrompt) {
        if (codexCloudWindowRef.current && !codexCloudWindowRef.current.closed) {
          codexCloudWindowRef.current.close();
        }
        codexCloudWindowRef.current = null;
        setCodexLoginPrompt(loginPrompt);
        addToast({
          kind: 'error',
          title: loginPrompt.title,
          message: loginPrompt.message,
        });
        return;
      }
      if (codexCloudWindowRef.current && !codexCloudWindowRef.current.closed) {
        codexCloudWindowRef.current.close();
      }
      codexCloudWindowRef.current = null;
      addToast({ kind: 'error', title: 'Could not open Codex', message: getApiErrorMessage(error, 'Could not open Codex.') });
    },
    onSettled: () => {
      setPendingCodexJobId(null);
    },
  });

  const archiveSessionMutation = useMutation({
    mutationFn: archiveSessionApi,
    onSuccess: (session) => {
      if (selectedSessionId === session.session_id && !showArchived) {
        setIsDraftMode(true);
        setSelectedSessionId(null);
      }
      queryClient.invalidateQueries({ queryKey: ['prd-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['prd-session', session.session_id] });
      addToast({ kind: 'success', title: 'Archived', message: 'Session archived from the active list.' });
    },
    onError: () => {
      addToast({ kind: 'error', title: 'Archive failed', message: 'Could not archive this PRD session.' });
    },
  });

  const restoreSessionMutation = useMutation({
    mutationFn: restoreSessionApi,
    onSuccess: (session) => {
      setIsDraftMode(false);
      setSelectedSessionId(session.session_id);
      queryClient.invalidateQueries({ queryKey: ['prd-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['prd-session', session.session_id] });
      addToast({ kind: 'success', title: 'Restored', message: 'Session restored to the active list.' });
    },
    onError: () => {
      addToast({ kind: 'error', title: 'Restore failed', message: 'Could not restore this PRD session.' });
    },
  });

  const deleteSessionMutation = useMutation({
    mutationFn: deleteSessionApi,
    onSuccess: (_data, sessionId) => {
      if (selectedSessionId === sessionId) {
        setIsDraftMode(true);
        setSelectedSessionId(null);
      }
      queryClient.invalidateQueries({ queryKey: ['prd-sessions'] });
      queryClient.removeQueries({ queryKey: ['prd-session', sessionId] });
      addToast({ kind: 'success', title: 'Deleted', message: 'Session deleted permanently.' });
    },
    onError: () => {
      addToast({ kind: 'error', title: 'Delete failed', message: 'Could not delete this PRD session.' });
    },
  });

  const selectedSession = activeSession;
  const clarificationState = selectedSession?.clarification_state || null;
  const hasStartedClarification = Boolean(clarificationState?.turns?.length);
  const activeOptimisticTurn = optimisticUserTurn
    && (!optimisticUserTurn.sessionId || optimisticUserTurn.sessionId === selectedSessionId)
    ? optimisticUserTurn
    : null;
  const visibleChatTurns: ClarificationTurn[] = [
    ...(clarificationState?.turns || []),
    ...(activeOptimisticTurn ? [{ role: 'user' as const, content: activeOptimisticTurn.content }] : []),
  ];
  const hasVisibleChat = visibleChatTurns.length > 0;
  const hasPendingAttachments = Boolean(
    selectedSession?.attachments?.some((attachment) => attachment.parse_status === 'queued' || attachment.parse_status === 'processing'),
  );
  const completedAttachments = (selectedSession?.attachments || []).filter(
    (attachment) => attachment.parse_status === 'completed',
  );
  const failedAttachments = (selectedSession?.attachments || []).filter(
    (attachment) => attachment.parse_status === 'failed',
  );
  const hasDraftSource = Boolean(draftMarkdown.trim() || completedAttachments.length);
  const draftExceedsLimit = draftMarkdown.length > MAX_DRAFT_CHARACTERS;
  const currentVersion = jobQuery.data?.latest_version || selectedSession?.latest_job?.latest_version || null;
  const reviewReadyKey = currentVersion ? `${currentVersion.session_id}:${currentVersion.id}` : null;
  const productSpecMarkedReady = Boolean(reviewReadyKey && reviewReadyByVersion[reviewReadyKey]);
  const qualityEvaluationKey = currentVersion ? `${currentVersion.session_id}:${currentVersion.id}` : null;
  const persistedQualityEvaluation = currentVersion?.export_metadata_json?.quality_evaluation as PRDQualityEvaluation | undefined;
  const currentQualityEvaluation = (qualityEvaluationKey ? qualityEvaluationsByVersion[qualityEvaluationKey] : null) || persistedQualityEvaluation;
  const generatedPrdSourceMarkdown = currentVersion?.executive_markdown?.trim()
    || currentVersion?.implementation_markdown?.trim()
    || '';
  const currentPrdMarkdown = prdOutputDirty ? prdOutputMarkdown : generatedPrdSourceMarkdown;
  const readinessScore = clarificationReadinessScore(clarificationState);
  const remainingGaps: string[] = Array.isArray(clarificationState?.reasoning?.readiness?.remaining_gaps)
    ? clarificationState?.reasoning?.readiness?.remaining_gaps.filter((item: unknown): item is string => typeof item === 'string' && Boolean(item.trim()))
    : [];
  const workingAssumptions = (clarificationState?.assumptions || [])
    .map((item) => item.trim())
    .filter(Boolean);
  const canGenerateWithAssumptions = Boolean(
    selectedSession
    && !selectedSession.ready_to_generate
    && readinessScore >= ASSUMPTION_READY_SCORE,
  );
  const generationBlocked = Boolean(
    generateMutation.isPending
    || hasPendingAttachments
    || draftExceedsLimit
    || !selectedCursorProvider
    || !selectedLlmProvider,
  );
  const codexRuns = codexRunsQuery.data?.items || [];
  const latestCodexRun = codexRuns[0] || null;
  const cloudCodexRun = codexRuns.find((run) => codexRunKind(run) === 'cloud') || null;
  const desktopCodexRun = codexRuns.find((run) => codexRunKind(run) === 'desktop') || null;
  const cloudCodexUrl = codexCloudUrlFromRun(cloudCodexRun);
  const cloudCodexUrlRecoveryAvailable = codexCloudRunNeedsUrlRecovery(cloudCodexRun);
  const latestCodexRunHasTemplatePaths = codexRunHasTemplatePaths(latestCodexRun);
  const latestCodexRunIsDesktop = codexRunKind(latestCodexRun) === 'desktop';
  const latestCodexRunDisplayComplete = Boolean(
    latestCodexRun?.status === 'completed'
    && (!latestCodexRunIsDesktop || latestCodexRunHasTemplatePaths),
  );
  const targetCodexRun = codexLaunchTarget === 'cloud' ? cloudCodexRun : desktopCodexRun;
  const codexRunActive = targetCodexRun ? !TERMINAL_STATUSES.has(targetCodexRun.status) : false;
  const codexCloudLaunchClaimed = Boolean(
    cloudCodexRun
    && !cloudCodexUrl
    && !cloudCodexUrlRecoveryAvailable,
  );
  const codexCloudAuthenticationInProgress = Boolean(
    pendingCodexCloudLaunch?.jobId === currentJobId,
  );
  const codexLaunchInProgress = Boolean(
    (codexLaunchMutation.isPending && pendingCodexJobId === currentJobId)
    || codexCloudAuthenticationInProgress,
  );
  const codexLaunchLabel = codexLaunchTarget === 'desktop'
    ? 'Open Codex Desktop'
    : cloudCodexUrl
      ? 'Open Codex Cloud'
      : cloudCodexRun && !cloudCodexUrlRecoveryAvailable
        ? 'Codex Cloud already launched'
        : 'Launch Codex Cloud';
  const codexCloudLogin = codexCloudLoginQuery.data || codexCloudLoginMutation.data;

  useEffect(() => {
    if (
      codexCloudLogin?.status !== 'authenticated'
      || !pendingCodexCloudLaunch
    ) return;
    const request = pendingCodexCloudLaunch;
    setPendingCodexCloudLaunch(null);
    setCodexCloudLoginPolling(false);
    setShowCodexCloudLogin(false);
    codexLaunchMutation.mutate(request);
  }, [codexCloudLogin?.status, pendingCodexCloudLaunch]); // eslint-disable-line react-hooks/exhaustive-deps

  const showCodexCloudOpeningNotice = () => {
    addToast({
      kind: 'info',
      title: 'Opening Codex Cloud',
      message: 'Wait about 5 seconds for the about:blank page to load the Codex Cloud run.',
      duration: 10000,
    });
  };

  const openCodexCloudTask = (cloudUrl: string) => {
    showCodexCloudOpeningNotice();
    const cloudWindow = window.open('about:blank', '_blank');
    if (cloudWindow) {
      cloudWindow.opener = null;
      cloudWindow.location.href = cloudUrl;
    } else {
      window.location.href = cloudUrl;
    }
  };

  const handleCodexLaunch = async () => {
    if (!currentJobId || !currentPrdMarkdown) return;
    if (codexLaunchTarget === 'cloud' && cloudCodexUrl) {
      openCodexCloudTask(cloudCodexUrl);
      return;
    }
    if (codexLaunchTarget === 'cloud' && cloudCodexRun && !cloudCodexUrlRecoveryAvailable) {
      addToast({
        kind: 'info',
        title: 'Codex Cloud already launched',
        message: 'This PRD chat session already has its one Codex Cloud task.',
      });
      return;
    }
    const request: CodexLaunchRequest = {
      jobId: currentJobId,
      prdMarkdown: currentPrdMarkdown,
      launchTarget: codexLaunchTarget,
    };
    if (codexLaunchTarget === 'desktop') {
      codexLaunchMutation.mutate(request);
      return;
    }

    showCodexCloudOpeningNotice();
    codexCloudWindowRef.current = window.open('about:blank', '_blank');
    if (codexCloudWindowRef.current) codexCloudWindowRef.current.opener = null;
    await queryClient.cancelQueries({ queryKey: ['prd-codex-cloud-login'] });
    codexCloudLoginMutation.reset();
    queryClient.removeQueries({ queryKey: ['prd-codex-cloud-login'] });
    setCodexCloudLoginPolling(false);
    setCodexLoginPrompt(null);
    setPendingCodexCloudLaunch({ ...request, interactiveLoginAttempted: true });
    setShowCodexCloudLogin(true);
    codexCloudLoginMutation.mutate();
  };

  const cancelCodexCloudLaunch = () => {
    queryClient.cancelQueries({ queryKey: ['prd-codex-cloud-login'] });
    if (codexCloudWindowRef.current && !codexCloudWindowRef.current.closed) {
      codexCloudWindowRef.current.close();
    }
    codexCloudWindowRef.current = null;
    setCodexCloudLoginPolling(false);
    setPendingCodexCloudLaunch(null);
    setShowCodexCloudLogin(false);
  };
  const effectiveJobStatus = jobQuery.data?.status || jobStatusQuery.data?.status || baseCurrentJobStatus || '';
  const activeProgress = jobQuery.data?.progress_percentage ?? selectedSession?.latest_job?.progress_percentage ?? 0;
  const activeStage = jobStatusQuery.data?.stage || jobQuery.data?.current_stage || selectedSession?.latest_job?.current_stage || 'idle';
  const generationFinalizing = Boolean(currentJobId && effectiveJobStatus === 'completed' && !currentVersion);
  const generationFailed = Boolean(currentJobId && effectiveJobStatus === 'failed' && !currentVersion);
  const generationErrorMessage = jobQuery.data?.error_message || jobStatusQuery.data?.error_message || selectedSession?.latest_job?.error_message || '';
  const generationInProgress = Boolean(
    (generateMutation.isPending && selectedPendingJobId)
    || generationFinalizing
    || (currentJobId && !TERMINAL_STATUSES.has(effectiveJobStatus)),
  );
  const prdWorkflowSteps = useMemo(() => {
    const launchStatus = codexLaunchInProgress
      ? 'active'
      : latestCodexRunDisplayComplete
        ? 'complete'
        : latestCodexRun?.status === 'failed'
          ? 'failed'
          : 'pending';

    return [
      {
        label: 'Generate Refined PRD',
        status: generationFailed ? 'failed' : generationInProgress ? 'active' : currentVersion ? 'complete' : 'pending',
        detail: generationInProgress
          ? `Running: ${String(activeStage || 'preparing').replace(/_/g, ' ')}`
          : generationFailed
            ? generationErrorMessage || 'Generation failed'
          : currentVersion
            ? 'Refined PRD generated'
            : selectedSession?.ready_to_generate
              ? 'Ready when you click Generate refined PRD'
              : 'Finish gap review first',
      },
      {
        label: 'Review Refined PRD',
        status: currentVersion ? (productSpecMarkedReady || launchStatus === 'complete' ? 'complete' : 'active') : 'pending',
        detail: currentVersion
          ? launchStatus === 'complete'
            ? 'Reviewed and handed off'
            : productSpecMarkedReady
              ? 'Marked ready for Codex handoff'
            : 'Edit the refined PRD before marking it ready for Codex.'
          : 'Available after generation',
      },
      {
        label: 'Open Codex',
        status: launchStatus,
        detail: launchStatus === 'complete'
          ? latestCodexRunIsDesktop ? 'Desktop handoff prepared' : 'Codex Cloud task submitted'
          : launchStatus === 'active'
            ? codexLaunchTarget === 'desktop' ? 'Opening Codex Desktop' : 'Submitting to Codex Cloud'
            : launchStatus === 'failed'
              ? latestCodexRun?.error_message || 'Handoff failed'
              : currentVersion
                ? `Final step: ${codexLaunchTarget === 'desktop' ? 'open Product Spec in Codex Desktop' : 'submit Product Spec to Codex Cloud'}`
                : 'Final step after review',
      },
    ] as Array<{ label: string; status: 'pending' | 'active' | 'complete' | 'failed'; detail: string }>;
  }, [
    activeStage,
    codexLaunchInProgress,
    codexLaunchTarget,
    currentVersion,
    generationErrorMessage,
    generationFailed,
    generationInProgress,
    latestCodexRun?.error_message,
    latestCodexRun?.status,
    latestCodexRunDisplayComplete,
    latestCodexRunIsDesktop,
    productSpecMarkedReady,
    selectedSession?.ready_to_generate,
  ]);
  const activeWorkflowStep = prdWorkflowSteps.find((step) => step.status === 'active')
    || [...prdWorkflowSteps].reverse().find((step) => step.status === 'complete')
    || prdWorkflowSteps[0];
  const cursorAgentUrl = jobQuery.data?.cursor_agent_url
    || jobStatusQuery.data?.cursor_agent_url
    || selectedSession?.latest_job?.cursor_agent_url
    || null;
  const cursorAgentStatus = effectiveJobStatus || (cursorAgentUrl ? 'completed' : 'queued');
  const desktopCodexUrl = codexDesktopUrlFromRun(desktopCodexRun);
  const sessionItems = sessionsQuery.data?.items || [];
  const writingModelOptions = useMemo(() => {
    const models = [
      selectedLlmProvider?.default_model,
      ...(selectedLlmProvider?.available_models || []),
      llmModel,
    ]
      .map((model) => String(model || '').trim())
      .filter(Boolean);
    return Array.from(new Set(models));
  }, [llmModel, selectedLlmProvider]);
  const storedDraftContext = String(selectedSession?.metadata_json?.external_context || '').trim();
  const draftSourceMarkdown = useMemo(
    () => storedDraftContext || (hasStartedClarification
      ? buildDraftMarkdown({
        title,
        versionTitle: currentVersion?.title,
        clarifiedBrief: clarificationState?.clarified_brief,
        resolvedRequirements: clarificationState?.resolved_requirements,
        assumptions: clarificationState?.assumptions,
      })
      : ''),
    [storedDraftContext, hasStartedClarification, title, currentVersion?.title, clarificationState],
  );
  const draftSourceKey = storedDraftContext
    ? [selectedSessionId || 'draft', storedDraftContext].join(':')
    : [
      selectedSessionId || 'draft',
      clarificationState?.turns?.length || 0,
      (clarificationState?.resolved_requirements || []).join('\u001f'),
      (clarificationState?.assumptions || []).join('\u001f'),
      clarificationState?.clarified_brief || '',
      draftSourceMarkdown,
    ].join(':');

  useEffect(() => {
    if (lastDraftSourceKeyRef.current === draftSourceKey) return;
    lastDraftSourceKeyRef.current = draftSourceKey;
    setDraftMarkdown(draftSourceMarkdown);
    setDraftDirty(false);
  }, [draftSourceKey, draftSourceMarkdown]);

  useEffect(() => {
    setPrdOutputMarkdown(generatedPrdSourceMarkdown);
    setPrdOutputDirty(false);
    setShowVersionCompare(false);
  }, [currentVersion?.id, generatedPrdSourceMarkdown]);

  const handleMarkProductSpecReady = () => {
    if (!reviewReadyKey) return;
    setReviewReadyByVersion((previous) => {
      const next = { ...previous, [reviewReadyKey]: true };
      saveReviewReadyState(next);
      return next;
    });
    addToast({
      kind: 'success',
      title: 'Marked ready',
      message: 'Review Product Spec is complete for this session.',
    });
  };

  const qualityEvaluationMarkdown = currentVersion ? currentPrdMarkdown : (draftMarkdown || draftSourceMarkdown);
  const qualityEvaluationMutation = useMutation({
    mutationFn: (input: { sessionId: string; versionKey: string; prdMarkdown: string }) =>
      evaluateSessionQuality(input.sessionId, input.prdMarkdown),
    networkMode: 'always',
    onSuccess: (evaluation, input) => {
      setQualityEvaluationsByVersion((previous) => {
        const next = { ...previous, [input.versionKey]: evaluation };
        saveQualityEvaluationState(next);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['prd-session', input.sessionId] });
      if (currentJobId) {
        queryClient.invalidateQueries({ queryKey: ['prd-job', currentJobId] });
      }
    },
  });
  const qualityEvaluationAppliesToCurrent = qualityEvaluationMutation.variables?.versionKey === qualityEvaluationKey;
  const qualityEvaluationPending = qualityEvaluationAppliesToCurrent && qualityEvaluationMutation.isPending;
  const qualityEvaluationError = qualityEvaluationAppliesToCurrent && qualityEvaluationMutation.isError;

  const handleUpdateQualityScore = () => {
    if (!selectedSessionId || !qualityEvaluationKey || !currentVersion) return;
    qualityEvaluationMutation.mutate({
      sessionId: selectedSessionId,
      versionKey: qualityEvaluationKey,
      prdMarkdown: qualityEvaluationMarkdown,
    });
  };

  useEffect(() => {
    if (
      !selectedSessionId
      || !qualityEvaluationKey
      || !currentVersion
      || currentQualityEvaluation
      || qualityEvaluationPending
      || autoScoredVersionKeysRef.current.has(qualityEvaluationKey)
    ) {
      return;
    }
    autoScoredVersionKeysRef.current.add(qualityEvaluationKey);
    qualityEvaluationMutation.mutate({
      sessionId: selectedSessionId,
      versionKey: qualityEvaluationKey,
      prdMarkdown: qualityEvaluationMarkdown,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSessionId, qualityEvaluationKey, currentVersion?.id, currentQualityEvaluation, qualityEvaluationPending]);

  const panelTabs: Array<{ id: PRDPanel; label: string; Icon: React.ComponentType<{ className?: string }> }> = [
    { id: 'setup', label: 'Setup', Icon: Cog6ToothIcon },
    { id: 'draft', label: 'Draft', Icon: DocumentArrowUpIcon },
    { id: 'chat', label: 'Gap Review', Icon: ChatBubbleLeftRightIcon },
    { id: 'context', label: 'Quality', Icon: CheckCircleIcon },
    { id: 'prd', label: 'Refined PRD', Icon: SparklesIcon },
  ];
  const activePanelTitle: Record<PRDPanel, string> = {
    setup: 'Setup',
    chat: 'Gap Review',
    draft: 'Draft Input',
    context: 'Quality Review',
    prd: 'Refined PRD',
  };

  const handleAnalyzeDraft = async () => {
    if (hasStartedClarification && !draftDirty) {
      setActivePanel('chat');
      return;
    }
    if (!hasDraftSource || hasPendingAttachments || draftExceedsLimit) {
      if (draftExceedsLimit) {
        addToast({
          kind: 'error',
          title: 'Draft is too long',
          message: `Shorten the draft to ${MAX_DRAFT_CHARACTERS.toLocaleString()} characters before continuing.`,
        });
      }
      return;
    }
    const request = buildGapAnalysisRequest(draftMarkdown, completedAttachments.length > 0);
    const latestDraftMarkdown = draftMarkdown.trim();
    setOptimisticUserTurn({ sessionId: activeSession?.session_id || null, content: request, submittedAt: Date.now() });
    setActivePanel('chat');
    try {
      if (activeSession?.session_id) {
        const session = await updateSessionConfiguration(
          activeSession.session_id,
          buildSessionPayload({
            title,
            repositoryUrl,
            branch,
            focusPathsText,
            outputMode,
            externalContextText: latestDraftMarkdown,
            cursorProviderConfigId,
            llmProviderConfigId,
            llmModel,
          }),
        );
        queryClient.setQueryData(['prd-session', session.session_id], session);
        queryClient.invalidateQueries({ queryKey: ['prd-sessions'] });
        setSavedDraftMarkdown(latestDraftMarkdown);
        setDraftDirty(false);
        await continueSessionMutation.mutateAsync(request);
        return;
      }
      await startSessionMutation.mutateAsync({
        request,
        title: title.trim() || null,
        repository_url: repositoryUrl,
        branch,
        focus_paths: parseFocusPaths(focusPathsText),
        output_mode: outputMode,
        external_context: latestDraftMarkdown || null,
        attachment_session_id: null,
        cursor_provider_config_id: cursorProviderConfigId ? Number(cursorProviderConfigId) : null,
        llm_provider_config_id: llmProviderConfigId ? Number(llmProviderConfigId) : null,
        llm_model: llmModel.trim() || null,
      });
      setSavedDraftMarkdown(latestDraftMarkdown);
      setDraftDirty(false);
    } catch {
      setOptimisticUserTurn(null);
      if (hasStartedClarification) setDraftDirty(true);
      setActivePanel('draft');
    }
  };

  const handleReply = async () => {
    const message = replyText.trim();
    const sessionId = activeSession?.session_id || selectedSessionId;
    if (!message || !sessionId) return;
    setOptimisticUserTurn({ sessionId, content: message, submittedAt: Date.now() });
    setReplyText('');
    try {
      await continueSessionMutation.mutateAsync(message);
    } catch {
      setReplyText(message);
    }
  };

  const downloadMarkdown = (markdown: string, filename: string) => {
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadMarkdown = () => {
    if (!currentPrdMarkdown.trim()) return;
    downloadMarkdown(currentPrdMarkdown, `${(currentVersion?.title || title || 'product-spec').replace(/\s+/g, '-').toLowerCase()}.md`);
  };

  const handleDownloadDraftMarkdown = () => {
    const markdown = draftMarkdown || draftSourceMarkdown;
    if (!markdown.trim()) return;
    downloadMarkdown(markdown, `${(title || currentVersion?.title || 'product-spec').replace(/\s+/g, '-').toLowerCase()}-draft.md`);
  };

  const handleCopyMarkdown = async () => {
    if (!currentPrdMarkdown.trim()) return;
    await navigator.clipboard.writeText(currentPrdMarkdown);
    addToast({ kind: 'success', title: 'Copied', message: 'Markdown copied to clipboard.' });
  };

  const handleCopyDraftMarkdown = async () => {
    const markdown = draftMarkdown || draftSourceMarkdown;
    if (!markdown.trim()) return;
    await navigator.clipboard.writeText(markdown);
    addToast({ kind: 'success', title: 'Copied', message: 'Draft markdown copied to clipboard.' });
  };

  const handleNewRefinement = () => {
    const previousSessionId = selectedSessionId;
    setIsDraftMode(true);
    setSelectedSessionId(null);
    setPendingJob(null);
    setPendingCodexJobId(null);
    setActivePanel('setup');
    setSetupExpanded(true);
    setTitle('');
    setRepositoryUrl(DEFAULT_REPO_URL);
    setBranch(DEFAULT_BRANCH);
    setFocusPathsText('');
    setSavedDraftMarkdown('');
    setOutputMode('product');
    setReplyText('');
    setDraftMarkdown('');
    setDraftDirty(false);
    setShowVersionCompare(false);
    if (previousSessionId) {
      queryClient.removeQueries({ queryKey: ['prd-session', previousSessionId] });
    }
  };

  return (
    <Layout>
      <input
        data-testid="prd-upload-input"
        ref={uploadInputRef}
        type="file"
        multiple
        className="hidden"
        accept=".xlsx,.csv,.pdf,.pptx,.ppt,.docx,.doc,.txt,.md,.html,.htm,.png,.jpg,.jpeg,.webp,.gif"
        onChange={async (event) => {
          const files = Array.from(event.target.files || []);
          if (!files.length) return;
          await uploadAttachmentMutation.mutateAsync(files);
        }}
      />

      <Modal open={showConfigModal} onClose={() => setShowConfigModal(false)}>
        <ModalBackdrop />
        <ModalContent className="max-w-2xl">
          <ModalHeader>
            <ModalTitle>PRD Refiner Configuration</ModalTitle>
          </ModalHeader>
          <ModalBody className="space-y-5">
            <div className="rounded-lg border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-surface-2 px-4 py-3">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Select the tenant AI providers used by this PRD session. The repository exploration
                provider must be Cursor-compatible; the writing provider handles clarification, findings parsing, and PRD synthesis.
              </p>
            </div>

            {providersQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Spinner size="sm" />
                Loading configured providers...
              </div>
            ) : (
              <>
                {cursorProviderOptions.length === 0 && (
                  <Alert variant="warning" title="Repository AI provider required">
                    Add or share an enabled Cursor-compatible AI provider from AI Providers before generating PRDs.
                  </Alert>
                )}
                {llmProviderOptions.length === 0 && (
                  <Alert variant="warning" title="Writing AI provider required">
                    Add or share an enabled OpenAI, Anthropic, Groq, or Bedrock provider before generating PRDs.
                  </Alert>
                )}
                {codexCloudProviderOptions.length === 0 && (
                  <Alert variant="warning" title="Codex Cloud provider unavailable">
                    Add an enabled Codex Cloud provider before launching tasks. Interactive ChatGPT login is still available.
                  </Alert>
                )}

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Repository exploration AI provider</Label>
                    <Select
                      value={selectedCursorProvider ? cursorProviderConfigId : ''}
                      onValueChange={setCursorProviderConfigId}
                      placeholder={repositoryProviderPlaceholder}
                      disabled={cursorProviderOptions.length === 0}
                    >
                      {cursorProviderOptions.map((provider) => (
                        <SelectOption key={provider.id} value={String(provider.id)}>
                          {providerDisplayName(provider, 'Cursor')}
                        </SelectOption>
                      ))}
                    </Select>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Used for repository exploration through Cursor Cloud.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label>PRD writing AI provider</Label>
                    <Select
                      value={selectedLlmProvider ? llmProviderConfigId : ''}
                      onValueChange={(value) => {
                        setLlmProviderConfigId(value);
                        const provider = llmProviderOptions.find((option) => String(option.id) === value);
                        setLlmModel(provider?.default_model || provider?.available_models?.[0] || '');
                      }}
                      placeholder={writingProviderPlaceholder}
                      disabled={llmProviderOptions.length === 0}
                    >
                      {llmProviderOptions.map((provider) => (
                        <SelectOption key={provider.id} value={String(provider.id)}>
                          {providerDisplayName(provider, humanizeProviderType(provider.provider_type))}
                        </SelectOption>
                      ))}
                    </Select>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Used for clarification, findings parsing, and PRD synthesis.
                    </p>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Writing model</Label>
                  <Select
                    value={llmModel}
                    onValueChange={setLlmModel}
                    placeholder={selectedLlmProvider ? 'Provider default model' : 'Select writing provider first'}
                    disabled={!selectedLlmProvider}
                  >
                    <SelectOption value="">Provider default</SelectOption>
                    {writingModelOptions.map((model) => (
                      <SelectOption key={model} value={model}>
                        {model}
                      </SelectOption>
                    ))}
                  </Select>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Choose a model from the selected writing provider, or use the provider default. The backend still adds any required LiteLLM provider prefix.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label>Codex Cloud provider</Label>
                  <Select
                    value={codexCloudProviderConfigId}
                    onValueChange={setCodexCloudProviderConfigId}
                    placeholder={codexCloudProviderPlaceholder}
                    disabled={codexCloudProviderOptions.length === 0}
                  >
                    {codexCloudProviderOptions.map((provider) => (
                      <SelectOption key={provider.id} value={String(provider.id)}>
                        {providerDisplayName(provider, 'Codex Cloud')}
                      </SelectOption>
                    ))}
                  </Select>
                </div>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setShowConfigModal(false)}>
              Cancel
            </Button>
            <Button
              variant="brand"
              onClick={() => updateConfigurationMutation.mutate()}
              disabled={
                updateConfigurationMutation.isPending
                || providersQuery.isLoading
                || !cursorProviderConfigId
                || !llmProviderConfigId
              }
            >
              {updateConfigurationMutation.isPending ? <Spinner size="sm" /> : null}
              Save Configuration
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal open={showCodexCloudLogin} onClose={cancelCodexCloudLaunch}>
        <ModalBackdrop />
        <ModalContent className="max-w-lg">
          <ModalHeader>
            <ModalTitle>Launch Codex Cloud</ModalTitle>
          </ModalHeader>
          <ModalBody className="space-y-4">
            {codexCloudLoginMutation.isPending && !codexCloudLogin ? (
              <div className="flex min-h-28 items-center justify-center">
                <Spinner size="sm" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-charcoal dark:text-gray-100">ChatGPT connection</span>
                  <Badge
                    variant={
                      codexCloudLogin?.status === 'authenticated'
                        ? 'success'
                        : codexCloudLogin?.status === 'failed'
                          ? 'danger'
                          : codexCloudLogin?.status === 'pending'
                            ? 'warning'
                            : 'secondary'
                    }
                  >
                    {codexCloudLogin?.status === 'authenticated'
                      ? 'Connected'
                      : codexCloudLogin?.status === 'failed'
                        ? 'Failed'
                        : codexCloudLogin?.status === 'pending'
                          ? 'Waiting for login'
                          : 'Not connected'}
                  </Badge>
                </div>

                <Alert
                  variant={
                    codexCloudLogin?.status === 'authenticated'
                      ? 'success'
                      : codexCloudLogin?.status === 'failed'
                        ? 'error'
                        : 'info'
                  }
                  title={codexCloudLogin?.status === 'authenticated' ? 'Codex Cloud connected' : undefined}
                >
                  {codexCloudLogin?.message || 'Start login to connect Codex Cloud with ChatGPT.'}
                </Alert>

                {pendingCodexCloudLaunch ? (
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    This Product Spec will launch automatically after ChatGPT authentication completes.
                  </p>
                ) : null}

                {codexCloudLogin?.user_code ? (
                  <div className="space-y-2">
                    <Label>One-time code</Label>
                    <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2">
                      <code className="min-w-0 flex-1 text-center text-xl font-semibold text-charcoal dark:text-gray-100">
                        {codexCloudLogin.user_code}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Copy one-time code"
                        onClick={async () => {
                          await navigator.clipboard.writeText(codexCloudLogin.user_code || '');
                          addToast({ kind: 'success', title: 'Copied', message: 'One-time code copied.' });
                        }}
                      >
                        <ClipboardDocumentIcon className="h-4 w-4" />
                        Copy
                      </Button>
                    </div>
                  </div>
                ) : null}

                {codexCloudLogin?.detail ? (
                  <p className="max-h-32 overflow-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-xs text-gray-500 dark:bg-dark-surface-2 dark:text-gray-400">
                    {codexCloudLogin.detail}
                  </p>
                ) : null}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={cancelCodexCloudLaunch}>
              Cancel
            </Button>
            {codexCloudLogin?.status === 'pending' && codexCloudLogin.verification_url ? (
              <a
                href={codexCloudLogin.verification_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-eliza-red px-3 text-sm font-medium text-white transition hover:bg-eliza-red/90"
              >
                Continue to ChatGPT
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </a>
            ) : null}
            {(!codexCloudLogin || codexCloudLogin.status === 'idle' || codexCloudLogin.status === 'failed') ? (
              <Button
                variant="brand"
                onClick={() => codexCloudLoginMutation.mutate()}
                disabled={codexCloudLoginMutation.isPending}
              >
                {codexCloudLoginMutation.isPending ? <Spinner size="sm" /> : <KeyIcon className="h-4 w-4" />}
                {codexCloudLogin?.status === 'failed' ? 'Try Again' : 'Start Login'}
              </Button>
            ) : null}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal open={Boolean(codexLoginPrompt)} onClose={() => setCodexLoginPrompt(null)}>
        <ModalBackdrop />
        <ModalContent className="max-w-2xl">
          <ModalHeader>
            <ModalTitle>{codexLoginPrompt?.title || 'Codex Cloud login required'}</ModalTitle>
          </ModalHeader>
          <ModalBody className="space-y-4">
            <Alert variant="warning" title={codexLoginPrompt?.alertTitle || 'One-time backend login needed'}>
              {codexLoginPrompt?.message || 'Complete Codex CLI login in the backend environment, then retry this handoff.'}
            </Alert>

            {(codexLoginPrompt?.localDockerCommand || codexLoginPrompt?.command) && (
              <div className="space-y-2">
                <Label>Local Docker command</Label>
                <pre className="max-h-40 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-200">
                  <code>{codexLoginPrompt.localDockerCommand || codexLoginPrompt.command}</code>
                </pre>
              </div>
            )}

            {codexLoginPrompt?.command && (
              <div className="space-y-2">
                <Label>Backend command</Label>
                <pre className="overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-200">
                  <code>{codexLoginPrompt.command}</code>
                </pre>
              </div>
            )}

            {codexLoginPrompt?.detail ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {codexLoginPrompt.detail}
              </p>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setCodexLoginPrompt(null)}>
              Close
            </Button>
            {(codexLoginPrompt?.localDockerCommand || codexLoginPrompt?.command) && (
              <Button
                variant="brand"
                onClick={async () => {
                  const command = codexLoginPrompt.localDockerCommand || codexLoginPrompt.command || '';
                  await navigator.clipboard.writeText(command);
                  addToast({ kind: 'success', title: 'Copied', message: 'Codex login command copied.' });
                }}
              >
                <ClipboardDocumentIcon className="h-4 w-4" />
                Copy Command
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Page layout="full-width">
        <PageHeader
          title="PRD Refiner"
          description="Start with an existing draft, close the highest-impact gaps, and produce a 90% thorough Product Spec."
          actions={(
            <Button variant="secondary" onClick={handleNewRefinement}>
              <PlusIcon className="h-4 w-4" />
              New Draft
            </Button>
          )}
        />

        <PageBody fill padded={false}>
          <div className="flex flex-1 min-h-0 gap-5 px-6 pb-6">

            {/* ===== LEFT SIDEBAR — Sessions ===== */}
            <aside
              className={`shrink-0 flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white transition-all duration-200 dark:border-dark-border dark:bg-dark-surface ${
                sessionsCollapsed ? 'w-14' : 'w-80'
              }`}
            >
              <div
                className={`flex h-[78px] items-center border-b border-gray-100 dark:border-dark-border/50 ${
                  sessionsCollapsed ? 'justify-center px-2' : 'justify-between px-5'
                }`}
              >
                {sessionsCollapsed ? (
                  <button
                    type="button"
                    aria-label="Expand sessions"
                    title="Expand sessions"
                    onClick={() => setSessionsCollapsed(false)}
                    className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-charcoal dark:hover:bg-dark-surface-2 dark:hover:text-gray-100"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                ) : (
                  <>
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Sessions
                    </span>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={showArchived}
                        onChange={(e) => setShowArchived(e.target.checked)}
                        label="Archived"
                        className="[&_label]:text-[11px] [&_label]:text-gray-400 [&_label]:dark:text-gray-500"
                      />
                      <button
                        type="button"
                        aria-label="Collapse sessions"
                        title="Collapse sessions"
                        onClick={() => setSessionsCollapsed(true)}
                        className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-charcoal dark:hover:bg-dark-surface-2 dark:hover:text-gray-100"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>

              {!sessionsCollapsed && (
              <div className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
                {sessionsQuery.isLoading ? (
                  <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-400 dark:text-gray-500">
                    <Spinner size="sm" /> Loading…
                  </div>
                ) : sessionsQuery.isError ? (
                  <div className="px-8 py-14 text-center text-sm leading-6 text-red-400">
                    Could not load sessions. Refresh or sign in again.
                  </div>
                ) : sessionItems.length === 0 ? (
                  <div className="px-3 py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                    No sessions yet.
                  </div>
                ) : (
                  sessionItems.map((session) => {
                    const isActive = !isDraftMode && selectedSessionId === session.session_id;
                    return (
                      <div
                        key={session.session_id}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setIsDraftMode(false);
                          setSelectedSessionId(session.session_id);
                          setActivePanel('draft');
                          setSetupExpanded(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setIsDraftMode(false);
                            setSelectedSessionId(session.session_id);
                            setActivePanel('draft');
                            setSetupExpanded(false);
                          }
                        }}
                        className={`group w-full text-left rounded-lg px-3 py-2.5 transition-colors cursor-pointer ${
                          isActive
                            ? 'border-l-[3px] border-l-eliza-red bg-eliza-red/5'
                            : 'border-l-[3px] border-l-transparent hover:bg-gray-50 dark:hover:bg-dark-surface-2'
                        } ${session.is_archived ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium text-charcoal dark:text-gray-100">
                            {session.title || 'Untitled'}
                          </span>
                          <Badge
                            variant={session.ready_to_generate ? 'success' : session.is_archived ? 'default' : 'warning'}
                            className="shrink-0 text-[10px]"
                          >
                            {session.is_archived ? 'Archived' : session.ready_to_generate ? 'Ready' : 'Draft'}
                          </Badge>
                        </div>
                        <div className="mt-1 truncate text-xs text-gray-400 dark:text-gray-500">
                          {session.repository_url || 'No repository'}
                        </div>
                        <div className="mt-1.5 flex items-center justify-between">
                          <span className="text-[11px] text-gray-400 dark:text-gray-500">
                            {formatTimestamp(session.updated_at)}
                          </span>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {session.is_archived ? (
                              <span
                                role="button"
                                tabIndex={0}
                                aria-label="Restore"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  restoreSessionMutation.mutate(session.session_id);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.stopPropagation();
                                    restoreSessionMutation.mutate(session.session_id);
                                  }
                                }}
                                className="text-[11px] text-gray-400 hover:text-charcoal dark:hover:text-gray-200 cursor-pointer select-none"
                              >
                                Restore
                              </span>
                            ) : (
                              <button
                                type="button"
                                aria-label="Archive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  archiveSessionMutation.mutate(session.session_id);
                                }}
                                className="p-0.5 rounded text-gray-400 hover:text-charcoal dark:hover:text-gray-200"
                              >
                                <ArchiveBoxIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <button
                              type="button"
                              aria-label="Delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(`Delete "${session.title}" permanently?`)) {
                                  deleteSessionMutation.mutate(session.session_id);
                                }
                              }}
                              className="p-0.5 rounded text-gray-400 hover:text-red-500"
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              )}
            </aside>

            {/* ===== MAIN CONTENT ===== */}
            <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
              <div className="shrink-0 flex h-[78px] items-center justify-between gap-4 border-b border-gray-200 dark:border-dark-border bg-white px-5 dark:bg-dark-surface">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-semibold text-charcoal dark:text-gray-100">{activePanelTitle[activePanel]}</h2>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <div className="flex items-center gap-1 rounded-xl bg-gray-100 dark:bg-dark-surface-2 p-1">
                    {panelTabs.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActivePanel(tab.id)}
                        className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                          activePanel === tab.id
                            ? 'bg-white text-charcoal shadow-sm dark:bg-dark-surface dark:text-gray-100'
                            : 'text-gray-500 hover:text-charcoal dark:text-gray-400 dark:hover:text-gray-100'
                        }`}
                      >
                        <tab.Icon className="mr-1.5 inline h-4 w-4 align-[-2px]" />
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* -- Setup -- */}
              {activePanel === 'setup' && (
                <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-dark-bg p-6">
                  <div className="mx-auto max-w-5xl rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-dark-border dark:bg-dark-surface">
                    <div className="mb-4 flex justify-end">
                      {!title.trim() ? <Badge variant="warning">Needs title</Badge> : null}
                    </div>

                    <div className="grid gap-6 xl:grid-cols-[1fr_0.95fr]">
                      <div className="space-y-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="prd-title">Title</Label>
                          <Input
                            id="prd-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Feature name"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="repository-url">Repository URL</Label>
                          <Input
                            id="repository-url"
                            value={repositoryUrl}
                            onChange={(e) => setRepositoryUrl(e.target.value)}
                            placeholder="https://github.com/org/repo"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="repository-branch">Branch</Label>
                          <Input
                            id="repository-branch"
                            value={branch}
                            onChange={(e) => setBranch(e.target.value)}
                            placeholder={DEFAULT_BRANCH}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="focus-paths">Focus product areas</Label>
                          <Textarea
                            id="focus-paths"
                            value={focusPathsText}
                            onChange={(e) => setFocusPathsText(e.target.value)}
                            placeholder={'Vendor spend insights\nAnomaly review workflow\nExecutive finance reporting'}
                            className="min-h-[116px] text-sm"
                          />
                        </div>
                      </div>

                      <div className="rounded-2xl bg-gray-950/5 p-5 dark:bg-dark-bg">
                        {providersQuery.isLoading ? (
                          <div className="flex h-full min-h-[220px] items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            <Spinner size="sm" />
                            Loading configured providers…
                          </div>
                        ) : providersQuery.isError ? (
                          <div className="rounded-xl border border-red-500/30 bg-red-950/20 p-5 text-red-200">
                            <div className="text-base font-semibold">Could not load AI providers</div>
                            <p className="mt-2 text-sm leading-6">
                              Refresh the page or sign in again. PRD Refiner could not read Admin Settings providers.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {cursorProviderOptions.length === 0 && (
                              <Alert variant="warning" title="Repository AI provider required">
                                Enable a Cursor-compatible provider in Admin Settings before generating PRDs.
                              </Alert>
                            )}
                            {llmProviderOptions.length === 0 && (
                              <Alert variant="warning" title="Writing AI provider required">
                                Enable a writing provider in Admin Settings before generating PRDs.
                              </Alert>
                            )}
                            {codexCloudProviderOptions.length === 0 && (
                              <Alert variant="warning" title="Codex Cloud provider unavailable">
                                Enable a Codex Cloud provider before launching tasks. Interactive ChatGPT login is still available.
                              </Alert>
                            )}

                            <div className="space-y-1.5">
                              <Label>Repository agent provider</Label>
                              <Select
                                value={selectedCursorProvider ? cursorProviderConfigId : ''}
                                onValueChange={setCursorProviderConfigId}
                                placeholder={repositoryProviderPlaceholder}
                                disabled={cursorProviderOptions.length === 0}
                              >
                                {cursorProviderOptions.map((provider) => (
                                  <SelectOption key={provider.id} value={String(provider.id)}>
                                    {providerDisplayName(provider, 'Cursor')}
                                  </SelectOption>
                                ))}
                              </Select>
                            </div>
                            <div className="space-y-1.5">
                              <Label>PRD writing provider</Label>
                              <Select
                                value={selectedLlmProvider ? llmProviderConfigId : ''}
                                onValueChange={(value) => {
                                  setLlmProviderConfigId(value);
                                  const provider = llmProviderOptions.find((option) => String(option.id) === value);
                                  setLlmModel(provider?.default_model || provider?.available_models?.[0] || '');
                                }}
                                placeholder={writingProviderPlaceholder}
                                disabled={llmProviderOptions.length === 0}
                              >
                                {llmProviderOptions.map((provider) => (
                                  <SelectOption key={provider.id} value={String(provider.id)}>
                                    {providerDisplayName(provider, humanizeProviderType(provider.provider_type))}
                                  </SelectOption>
                                ))}
                              </Select>
                            </div>
                            <div className="space-y-1.5">
                              <Label>Writing model</Label>
                              <Select
                                value={llmModel}
                                onValueChange={setLlmModel}
                                placeholder={selectedLlmProvider ? 'Provider default model' : 'Select writing provider first'}
                                disabled={!selectedLlmProvider}
                              >
                                <SelectOption value="">Provider default</SelectOption>
                                {writingModelOptions.map((model) => (
                                  <SelectOption key={model} value={model}>
                                    {model}
                                  </SelectOption>
                                ))}
                              </Select>
                            </div>
                            <div className="space-y-1.5">
                              <Label>Codex Cloud provider</Label>
                              <Select
                                value={codexCloudProviderConfigId}
                                onValueChange={setCodexCloudProviderConfigId}
                                placeholder={codexCloudProviderPlaceholder}
                                disabled={codexCloudProviderOptions.length === 0}
                              >
                                {codexCloudProviderOptions.map((provider) => (
                                  <SelectOption key={provider.id} value={String(provider.id)}>
                                    {providerDisplayName(provider, 'Codex Cloud')}
                                  </SelectOption>
                                ))}
                              </Select>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-2 border-t border-gray-200 pt-4 dark:border-dark-border">
                      <Button
                        variant="secondary"
                        disabled={
                          updateConfigurationMutation.isPending
                          || providersQuery.isLoading
                          || !cursorProviderConfigId
                          || !llmProviderConfigId
                        }
                        onClick={() => updateConfigurationMutation.mutate()}
                      >
                        {updateConfigurationMutation.isPending ? <Spinner size="sm" /> : null}
                        Save Setup
                      </Button>
                      <Button
                        variant="brand"
                        disabled={!title.trim()}
                        onClick={() => setActivePanel('draft')}
                      >
                        Continue to Draft
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {activePanel === 'chat' && (
                <>
                  <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-dark-bg px-6 py-6">
                    {!hasVisibleChat ? (
                      <div className="flex h-full flex-col items-center justify-center text-center">
                        <DocumentMagnifyingGlassIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
                        <h3 className="mt-4 font-subtitle text-lg text-charcoal dark:text-gray-100">
                          Add a PRD draft first
                        </h3>
                        <p className="mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
                          Paste a draft or attach a document. Gap Review will then identify missing decisions and ask focused follow-up questions.
                        </p>
                        <Button variant="brand" className="mt-5" onClick={() => setActivePanel('draft')}>
                          <DocumentArrowUpIcon className="h-4 w-4" />
                          Go to Draft
                        </Button>
                      </div>
                    ) : (
                      <div className="mx-auto max-w-5xl space-y-5">
                        <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-dark-border dark:bg-dark-surface">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                PRD thoroughness
                              </div>
                              <div className="mt-1 text-2xl font-semibold text-charcoal dark:text-gray-100">
                                {readinessScore}%
                              </div>
                            </div>
                            <Badge variant={readinessScore >= TARGET_READINESS_SCORE ? 'success' : 'info'}>
                              Target {TARGET_READINESS_SCORE}%
                            </Badge>
                          </div>
                          <Progress value={readinessScore} className="mt-3" />
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            The score rises as material gaps are answered. Low-risk unknowns can remain as explicit assumptions.
                          </p>
                        </section>

                        {(remainingGaps.length > 0 || workingAssumptions.length > 0) && (
                          <div className="grid gap-4 md:grid-cols-2">
                            <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
                              <div className="flex items-center justify-between gap-3">
                                <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">Open gaps</h3>
                                <Badge variant={remainingGaps.length ? 'warning' : 'success'}>{remainingGaps.length}</Badge>
                              </div>
                              {remainingGaps.length ? (
                                <ul className="mt-3 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                                  {remainingGaps.map((gap) => <li key={gap}>• {gap}</li>)}
                                </ul>
                              ) : (
                                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No material gaps are open.</p>
                              )}
                            </section>
                            <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
                              <div className="flex items-center justify-between gap-3">
                                <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">Working assumptions</h3>
                                <Badge variant="secondary">{workingAssumptions.length}</Badge>
                              </div>
                              {workingAssumptions.length ? (
                                <ul className="mt-3 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                                  {workingAssumptions.map((assumption) => <li key={assumption}>• {assumption}</li>)}
                                </ul>
                              ) : (
                                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No assumptions recorded yet.</p>
                              )}
                            </section>
                          </div>
                        )}

                        {visibleChatTurns.map((turn, index) => (
                          <div
                            key={`${turn.role}-${index}`}
                            className={`flex gap-3 ${turn.role === 'user' ? 'flex-row-reverse' : ''}`}
                          >
                            <div
                              className={`mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                                turn.role === 'assistant'
                                  ? 'bg-gray-200 dark:bg-dark-surface-2'
                                  : 'bg-eliza-red/10'
                              }`}
                            >
                              {turn.role === 'assistant' ? (
                                <SparklesIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                              ) : (
                                <span className="text-xs font-semibold text-eliza-red">Y</span>
                              )}
                            </div>
                            <div
                              className={`max-w-[82%] rounded-2xl px-4 py-3 ${
                                turn.role === 'assistant'
                                  ? 'rounded-tl-md border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface'
                                  : 'rounded-tr-md bg-eliza-red/5 dark:bg-eliza-red/10'
                              }`}
                            >
                              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                {turn.role === 'assistant' ? 'PRD Refiner' : 'You'}
                              </div>
                              <div className="whitespace-pre-wrap text-sm leading-relaxed text-charcoal dark:text-gray-100">
                                {clarificationTurnDisplayContent(turn)}
                              </div>
                            </div>
                          </div>
                        ))}

                        {(startSessionMutation.isPending || continueSessionMutation.isPending) && (
                          <div className="flex gap-3">
                            <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 dark:bg-dark-surface-2">
                              <SparklesIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                            </div>
                            <div className="rounded-2xl rounded-tl-md border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500 dark:border-dark-border dark:bg-dark-surface dark:text-gray-400">
                              <span className="inline-flex items-center gap-2">
                                <Spinner size="sm" />
                                Analyzing the draft and preparing gap questions…
                              </span>
                            </div>
                          </div>
                        )}

                        {selectedSession?.ready_to_generate && (
                          <div className="flex justify-center gap-2 py-2">
                            <div className="inline-flex items-center gap-2 rounded-full bg-green-50 px-4 py-2 text-sm font-medium text-green-700 dark:bg-green-500/10 dark:text-green-400">
                              <CheckCircleIcon className="h-4 w-4" />
                              {TARGET_READINESS_SCORE}%+ thorough
                            </div>
                            <Button
                              variant="brand"
                              size="sm"
                              onClick={() => generateMutation.mutate({ force: false })}
                              disabled={generationBlocked}
                            >
                              {generateMutation.isPending ? <Spinner size="sm" /> : <SparklesIcon className="h-4 w-4" />}
                              Generate refined PRD
                            </Button>
                          </div>
                        )}

                        {canGenerateWithAssumptions && (
                          <div className="flex justify-center gap-2 py-2">
                            <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                              <SparklesIcon className="h-4 w-4" />
                              Ready with assumptions ({readinessScore}%)
                            </div>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => generateMutation.mutate({ force: true })}
                              disabled={generationBlocked}
                            >
                              Generate refined PRD with assumptions
                            </Button>
                          </div>
                        )}

                        <div ref={chatEndRef} />
                      </div>
                    )}
                  </div>

                  {hasStartedClarification && !selectedSession?.ready_to_generate && (
                    <div className="shrink-0 border-t border-gray-200 bg-white px-6 py-4 dark:border-dark-border dark:bg-dark-surface">
                      <div className="mx-auto flex max-w-5xl items-end gap-3">
                        <Textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Answer the latest gap questions..."
                          className="max-h-[160px] min-h-[48px] flex-1"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                              e.preventDefault();
                              if (replyText.trim() && !continueSessionMutation.isPending) handleReply();
                            }
                          }}
                        />
                        <Button
                          variant="brand"
                          onClick={handleReply}
                          disabled={!replyText.trim() || continueSessionMutation.isPending}
                          className="shrink-0"
                        >
                          {continueSessionMutation.isPending ? <Spinner size="sm" /> : <PaperAirplaneIcon className="h-4 w-4" />}
                          Send
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {activePanel === 'draft' && (
                <div className="flex-1 overflow-y-auto bg-gray-50 p-6 dark:bg-dark-bg">
                  <div className="mx-auto max-w-6xl">
                    <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-dark-border dark:bg-dark-surface">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h3 className="text-base font-semibold text-charcoal dark:text-gray-100">Import your current PRD</h3>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Edit the markdown directly, attach a document, or use both. This becomes the original version for gap analysis.
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="secondary" size="sm" onClick={handleCopyDraftMarkdown} disabled={!draftMarkdown.trim()}>
                            <ClipboardDocumentIcon className="h-4 w-4" />
                            Copy
                          </Button>
                          <Button variant="secondary" size="sm" onClick={handleDownloadDraftMarkdown} disabled={!draftMarkdown.trim()}>
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            Download
                          </Button>
                        </div>
                      </div>

                      <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
                        <div>
                          <Label htmlFor="prd-draft-input">Inline draft</Label>
                          <Textarea
                            id="prd-draft-input"
                            aria-label="PRD draft"
                            value={draftMarkdown}
                            maxLength={MAX_DRAFT_CHARACTERS}
                            onChange={(event) => {
                              setDraftMarkdown(event.target.value);
                              setDraftDirty(true);
                            }}
                            className="mt-2 min-h-[54vh] resize-y rounded-2xl border-gray-200 bg-gray-50 font-mono text-sm leading-6 text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
                            placeholder="# Product Requirements Document\n\nPaste your existing draft here..."
                          />
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            {draftDirty ? <Badge variant="warning">Edited</Badge> : <Badge variant="secondary">Original</Badge>}
                            <span>Markdown is supported.</span>
                            <span className="ml-auto" aria-label="Draft character count">
                              {draftMarkdown.length.toLocaleString()} / {MAX_DRAFT_CHARACTERS.toLocaleString()} characters
                            </span>
                          </div>
                          {draftMarkdown.length === MAX_DRAFT_CHARACTERS ? (
                            <Alert variant="warning" title="Draft character limit reached" className="mt-3">
                              This draft is at the API limit. Shorten it before adding more inline content, or attach the remaining material as a document.
                            </Alert>
                          ) : null}
                        </div>

                        <aside className="space-y-3">
                          <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-5 text-center dark:border-dark-border dark:bg-dark-surface-2">
                            <DocumentArrowUpIcon className="mx-auto h-8 w-8 text-gray-400" />
                            <h4 className="mt-3 text-sm font-semibold text-charcoal dark:text-gray-100">Attach a PRD document</h4>
                            <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                              PDF, DOCX, markdown, text, slides, spreadsheets, or images.
                            </p>
                            <Button
                              variant="secondary"
                              size="sm"
                              className="mt-4"
                              onClick={() => uploadInputRef.current?.click()}
                              disabled={uploadAttachmentMutation.isPending}
                            >
                              {uploadAttachmentMutation.isPending ? <Spinner size="sm" /> : <PaperClipIcon className="h-4 w-4" />}
                              Choose document
                            </Button>
                          </div>

                          {(selectedSession?.attachments || []).map((attachment) => (
                            <div key={attachment.id} className="rounded-xl border border-gray-200 bg-white p-3 dark:border-dark-border dark:bg-dark-surface">
                              <div className="flex items-start justify-between gap-2">
                                <span className="min-w-0 truncate text-sm font-medium text-charcoal dark:text-gray-100" title={attachment.original_filename}>
                                  {attachment.original_filename}
                                </span>
                                <Badge
                                  variant={attachment.parse_status === 'completed'
                                    ? 'success'
                                    : attachment.parse_status === 'failed'
                                      ? 'danger'
                                      : 'warning'}
                                >
                                  {attachment.parse_status}
                                </Badge>
                              </div>
                              {attachment.parse_error ? (
                                <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">{attachment.parse_error}</p>
                              ) : null}
                            </div>
                          ))}

                          {failedAttachments.length > 0 && (
                            <Alert variant="warning" title="Some documents could not be read">
                              Re-upload the failed files or paste their content into the inline draft.
                            </Alert>
                          )}

                          <div className="rounded-xl border border-gray-200 p-3 dark:border-dark-border">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Version start</div>
                            <div className="mt-2 flex items-center gap-2">
                              <Badge variant="secondary">Original</Badge>
                              <span className="text-xs text-gray-500 dark:text-gray-400">Source draft</span>
                            </div>
                          </div>
                        </aside>
                      </div>

                      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4 dark:border-dark-border">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {hasPendingAttachments
                            ? 'Preparing the attached document…'
                            : hasStartedClarification
                              ? `Gap review is in progress at ${readinessScore}% thoroughness.`
                              : hasDraftSource
                                ? 'Ready to identify gaps in this draft.'
                                : 'Paste a draft or attach a document to continue.'}
                        </span>
                        <Button
                          variant="brand"
                          onClick={handleAnalyzeDraft}
                          disabled={
                            !hasDraftSource
                            || hasPendingAttachments
                            || draftExceedsLimit
                            || startSessionMutation.isPending
                            || continueSessionMutation.isPending
                            || !selectedCursorProvider
                            || !selectedLlmProvider
                          }
                        >
                          {(startSessionMutation.isPending || continueSessionMutation.isPending)
                            ? <Spinner size="sm" />
                            : <DocumentMagnifyingGlassIcon className="h-4 w-4" />}
                          {hasStartedClarification ? 'Continue gap review' : 'Analyze gaps'}
                        </Button>
                      </div>
                    </section>
                  </div>
                </div>
              )}

              {activePanel === 'context' && (
                <div className="flex-1 overflow-y-auto bg-gray-50 p-6 dark:bg-dark-bg">
                  <div className="mx-auto grid max-w-6xl gap-4">
                    <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-dark-border dark:bg-dark-surface">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">PRD Output Score</h3>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Scores the generated PRD for product clarity, completeness, and section quality.
                          </p>
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleUpdateQualityScore}
                          disabled={!selectedSessionId || !currentVersion || qualityEvaluationPending}
                        >
                          {qualityEvaluationPending ? <Spinner size="sm" /> : <SparklesIcon className="h-4 w-4" />}
                          Update Score
                        </Button>
                      </div>

                      <div className="mt-4">
                        {!currentVersion ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Generate a PRD to see quality score details.
                          </p>
                        ) : qualityEvaluationPending && !currentQualityEvaluation ? (
                          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            <Spinner size="sm" />
                            Evaluating PRD output...
                          </div>
                        ) : qualityEvaluationError && !currentQualityEvaluation ? (
                          <Alert variant="warning" title="Could not evaluate PRD output">
                            The evaluator could not score this draft right now. Refresh the score after the provider is available.
                          </Alert>
                        ) : currentQualityEvaluation ? (
                          <div className="grid gap-4 lg:grid-cols-[180px_1fr]">
                            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-center dark:border-dark-border dark:bg-dark-surface-2">
                              <div className="text-4xl font-semibold text-charcoal dark:text-gray-100">
                                {currentQualityEvaluation.score}
                              </div>
                              <div className="mt-1 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">out of 100</div>
                              <Badge
                                variant={currentQualityEvaluation.passed ? 'success' : 'warning'}
                                className="mt-3"
                              >
                                {currentQualityEvaluation.readiness}
                              </Badge>
                            </div>

                            <div className="space-y-4">
                              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                {Object.entries(currentQualityEvaluation.dimension_scores || {}).map(([key, value]) => (
                                  <div key={key} className="rounded-xl border border-gray-200 p-3 dark:border-dark-border">
                                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                      {formatQualityDimension(key)}
                                    </div>
                                    <div className="mt-1 text-lg font-semibold text-charcoal dark:text-gray-100">{value}/100</div>
                                  </div>
                                ))}
                              </div>

                              <div className="grid gap-4 md:grid-cols-2">
                                <div>
                                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Strengths</h4>
                                  <ul className="mt-2 space-y-1 text-sm text-charcoal dark:text-gray-100">
                                    {(currentQualityEvaluation.strengths.length ? currentQualityEvaluation.strengths : ['No strengths returned yet.']).map((item) => (
                                      <li key={item}>- {item}</li>
                                    ))}
                                  </ul>
                                </div>
                                <div>
                                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Issues</h4>
                                  <ul className="mt-2 space-y-1 text-sm text-charcoal dark:text-gray-100">
                                    {(currentQualityEvaluation.issues.length ? currentQualityEvaluation.issues : ['No issues returned yet.']).map((item) => (
                                      <li key={item}>- {item}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>

                              {Object.keys(currentQualityEvaluation.section_scores || {}).length > 0 && (
                                <div>
                                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Section Breakdown
                                  </h4>
                                  <div className="mt-2 grid gap-2 md:grid-cols-2">
                                    {Object.entries(currentQualityEvaluation.section_scores || {}).map(([section, value]) => (
                                      <div key={section} className="rounded-xl border border-gray-200 p-3 dark:border-dark-border">
                                        <div className="flex items-center justify-between gap-3">
                                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                            {formatQualityDimension(section)}
                                          </div>
                                          <div className="text-sm font-semibold text-charcoal dark:text-gray-100">{value}/100</div>
                                        </div>
                                        {(currentQualityEvaluation.section_feedback?.[section] || []).length > 0 && (
                                          <ul className="mt-2 space-y-1 text-xs text-gray-500 dark:text-gray-400">
                                            {(currentQualityEvaluation.section_feedback?.[section] || []).map((item) => (
                                              <li key={item}>- {item}</li>
                                            ))}
                                          </ul>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Click Update Score to run the PRD output evaluator.
                          </p>
                        )}
                      </div>
                    </section>
                  </div>
                </div>
              )}

              {activePanel === 'prd' && (
                <div className="flex-1 overflow-y-auto bg-gray-50 p-6 dark:bg-dark-bg">
                  <div className="mx-auto max-w-6xl">
                    <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-dark-border dark:bg-dark-surface">
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div>
                          <h3 className="text-base font-semibold text-charcoal dark:text-gray-100">Refined PRD</h3>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {currentVersion ? currentVersion.title : 'No refined PRD yet'}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant={showVersionCompare ? 'secondary' : 'ghost'}
                            size="sm"
                            onClick={() => setShowVersionCompare((current) => !current)}
                            disabled={!currentPrdMarkdown.trim() || !draftMarkdown.trim()}
                          >
                            <ArrowsRightLeftIcon className="h-4 w-4" />
                            {showVersionCompare ? 'Edit refined PRD' : 'Compare versions'}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={handleCopyMarkdown} disabled={!currentPrdMarkdown.trim()}>
                            <ClipboardDocumentIcon className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={handleDownloadMarkdown} disabled={!currentPrdMarkdown.trim()}>
                            <ArrowDownTrayIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-xs dark:border-dark-border dark:bg-dark-surface-2">
                        <Badge variant="secondary">Original</Badge>
                        <span className="text-gray-400">→</span>
                        {currentVersion ? (
                          <>
                            <Badge variant="success">Refined v{currentVersion.version_number}</Badge>
                            <span className="text-gray-500 dark:text-gray-400">{formatTimestamp(currentVersion.created_at)}</span>
                          </>
                        ) : (
                          <Badge variant="outline">Refined version pending</Badge>
                        )}
                      </div>

                      <div className="mb-5 rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              Status
                            </div>
                            <div className="mt-1 text-sm font-medium text-charcoal dark:text-gray-100">
                              Current step: {activeWorkflowStep.label}
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 grid gap-2 md:grid-cols-3">
                          {prdWorkflowSteps.map((step, index) => (
                            <div
                              key={step.label}
                              className={`rounded-xl border p-3 ${
                                step.status === 'complete'
                                  ? 'border-green-200 bg-green-50 text-green-900 dark:border-green-900/50 dark:bg-green-950/30 dark:text-green-100'
                                  : step.status === 'active'
                                    ? 'border-eliza-red/30 bg-eliza-red/10 text-charcoal dark:border-eliza-red/40 dark:bg-eliza-red/15 dark:text-gray-100'
                                    : step.status === 'failed'
                                      ? 'border-red-200 bg-red-50 text-red-900 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-100'
                                      : 'border-gray-200 bg-white text-gray-600 dark:border-dark-border dark:bg-dark-surface dark:text-gray-300'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/70 text-xs font-semibold dark:bg-dark-surface">
                                  {step.status === 'complete' ? (
                                    <CheckCircleIcon className="h-4 w-4" />
                                  ) : step.status === 'active' ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    index + 1
                                  )}
                                </span>
                                <span className="text-sm font-semibold">{step.label}</span>
                              </div>
                              <div className="mt-2 text-xs leading-5 opacity-80">{step.detail}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="min-h-[190px]">
                        {currentVersion || currentPrdMarkdown ? (
                          showVersionCompare && draftMarkdown.trim() ? (
                            <DiffViewer
                              oldText={draftMarkdown}
                              newText={currentPrdMarkdown}
                              oldLabel="Original draft"
                              newLabel={currentVersion ? `Refined v${currentVersion.version_number}` : 'Refined PRD'}
                              mode="side-by-side"
                              className="min-h-[56vh]"
                            />
                          ) : (
                            <Textarea
                              aria-label="Refined PRD markdown"
                              value={prdOutputMarkdown}
                              onChange={(event) => {
                                setPrdOutputMarkdown(event.target.value);
                                setPrdOutputDirty(true);
                              }}
                              className="min-h-[56vh] resize-y rounded-2xl border-gray-200 bg-gray-50 font-mono text-sm leading-6 text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
                              placeholder="# Product Requirements Document"
                            />
                          )
                        ) : (
                          <div className="flex min-h-[190px] items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50 text-sm text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
                            No refined PRD yet
                          </div>
                        )}
                      </div>
                      {currentVersion && (
                        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          {prdOutputDirty ? <Badge variant="warning">Edited</Badge> : <Badge variant="default">Generated</Badge>}
                          <span>Open Codex uses the markdown currently shown in this editor.</span>
                        </div>
                      )}

                      <div className="mt-5 border-t border-gray-200 pt-4 dark:border-dark-border">
                        {generationInProgress && (
                          <div className="mb-4 space-y-2">
                            <div className="flex items-center justify-between gap-3">
                              <span className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                <Spinner size="sm" />
                                Generating refined PRD…
                              </span>
                              <span className="text-sm capitalize text-gray-500 dark:text-gray-400">
                                {String(activeStage || 'idle').replace(/_/g, ' ')}
                              </span>
                            </div>
                            <Progress value={activeProgress} />
                          </div>
                        )}

                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            variant={productSpecMarkedReady ? 'secondary' : 'brand'}
                            disabled={!currentVersion}
                            onClick={handleMarkProductSpecReady}
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            {productSpecMarkedReady ? 'Ready' : 'Mark Ready'}
                          </Button>
                          {cursorAgentUrl ? (
                            <a
                              href={cursorAgentUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-charcoal shadow-sm transition hover:bg-gray-50 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100 dark:hover:bg-dark-surface"
                            >
                              Open Cursor Agent
                            </a>
                          ) : (
                            <Button variant="secondary" disabled>
                              Open Cursor Agent
                            </Button>
                          )}
                          <Select
                            value={codexLaunchTarget}
                            onValueChange={(value) => setCodexLaunchTarget(value as CodexLaunchTarget)}
                            className="w-48 min-w-[12rem]"
                          >
                            <SelectOption value="cloud">Codex Cloud</SelectOption>
                            <SelectOption value="desktop">Codex Desktop</SelectOption>
                          </Select>
                          <Button
                            variant="secondary"
                            onClick={handleCodexLaunch}
                            disabled={
                              !currentPrdMarkdown
                              || !currentJobId
                              || codexRunActive
                              || codexLaunchInProgress
                              || (codexLaunchTarget === 'cloud' && codexCloudLaunchClaimed)
                            }
                          >
                            {codexLaunchInProgress ? <Spinner size="sm" /> : null}
                            {codexLaunchLabel}
                          </Button>
                        </div>
                        {(currentJobId || cloudCodexRun || desktopCodexRun) && (
                          <div className="mt-3 divide-y divide-gray-200 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-gray-600 dark:divide-dark-border dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-300">
                            {currentJobId ? (
                              <div className="flex flex-wrap items-center gap-3 py-2">
                                <span>
                                  Cursor Agent:{' '}
                                  <span className="font-medium capitalize">{cursorAgentStatus}</span>
                                </span>
                                {cursorAgentUrl ? (
                                  <a
                                    href={cursorAgentUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="font-medium text-eliza-red hover:underline"
                                  >
                                    Open Cursor Agent
                                  </a>
                                ) : null}
                              </div>
                            ) : null}
                            {cloudCodexRun ? (
                              <div className="flex flex-wrap items-center gap-3 py-2">
                                <span>
                                  Codex Cloud task:{' '}
                                  <span className="font-medium capitalize">{cloudCodexRun.status}</span>
                                </span>
                                {cloudCodexUrl ? (
                                  <a
                                    href={cloudCodexUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      openCodexCloudTask(cloudCodexUrl);
                                    }}
                                    className="font-medium text-eliza-red hover:underline"
                                  >
                                    Open Codex Cloud
                                  </a>
                                ) : null}
                                {cloudCodexRun.error_message ? <span className="text-red-400">{cloudCodexRun.error_message}</span> : null}
                              </div>
                            ) : null}
                            {desktopCodexRun ? (
                              <div className="flex flex-wrap items-center gap-3 py-2">
                                <span>
                                  Codex Desktop handoff:{' '}
                                  <span className="font-medium capitalize">{desktopCodexRun.status}</span>
                                </span>
                                {desktopCodexUrl ? (
                                  <a
                                    href={desktopCodexUrl}
                                    className="font-medium text-eliza-red hover:underline"
                                  >
                                    Open Codex Desktop
                                  </a>
                                ) : null}
                                {desktopCodexRun.error_message ? <span className="text-red-400">{desktopCodexRun.error_message}</span> : null}
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </section>
                  </div>
                </div>
              )}
            </div>

          </div>
        </PageBody>
      </Page>
    </Layout>
  );
}
